<template>
  <div class="op-page" :class="{ 'op-page--msg': subKey === 'message' }">
    <!-- ============================================================
         【P314】未登录态：内联登录层（不跳转，直接停在 #/operation）
         需求：https://liubai.cs8.simpleappeal.cn/#/operation 增加登录页，
               退出后直接回到本页的登录态（而不是跳到 #/login 或显示「无权限」）。
         做法与市场端（P120）完全一致：窄屏 ClientLogin / 宽屏 PcLogin。
         ============================================================ -->
    <client-login v-if="showLogin && innerWidth <= 730" @success="onLoginSuccess" />
    <pc-login v-else-if="showLogin" />

    <!-- 以下为已登录后的全部业务内容（未登录时整块不渲染，避免闪「无权限」）-->
    <template v-else>
    <!-- ============================================================
         A. 权限不足态
         ============================================================ -->
    <div class="op-denied" v-if="authDenied">
      <div class="op-denied__ic"><van-icon name="warning-o" /></div>
      <div class="op-denied__t">无运营端访问权限</div>
      <div class="op-denied__m">{{ authMsg }}</div>
      <div class="op-denied__card">
        <div class="op-denied__row"><span>账号</span><b>{{ profile.name || '-' }}</b></div>
        <div class="op-denied__row"><span>角色</span><b>{{ roleText || '-' }}</b></div>
        <div class="op-denied__row"><span>部门</span><b>{{ deptText || '-' }}</b></div>
      </div>
      <div class="op-denied__hint">
        运营端开放给：运营人员（角色「运营」）/ 运营部门成员 / 管理员。如需开通请联系系统管理员。
      </div>
      <van-button block type="primary" round @click="loadProfile">重新检测</van-button>
      <van-button block plain round @click="onLogout">退出登录</van-button>
    </div>

    <template v-else>
      <!-- ============================================================
           B. 快捷入口 Tab（原「首页」内容，【P176】房态占用 Tab0 后并入此处）
              画像 + KPI 看板 + 快捷九宫格
              【P235 修正】★ 本块判据 tabIndex===4 —— TABS 只有 5 项（索引 0~4）：
                0房态 1会议室订单 2增值订单 3采购单 4快捷入口。
                之前误写成 5 ⇒ **快捷入口页永远不渲染**（Tab 第 5 项点不动、九宫格不出现）。
                消息中心已改为 subKey 子页，不再占 Tab 索引，两者不再冲突。
           ============================================================ -->
      <template v-if="!subKey && tabIndex === 4">
        <!-- 【P112】下拉刷新：重拉 KPI 看板 + 未读角标 -->
        <van-pull-refresh v-model="homeRefreshing" @refresh="onHomeRefresh">
        <div class="op-hero">
          <div class="op-hero__top">
            <span class="op-avatar">{{ avatarText }}</span>
            <div class="op-hero__id">
              <div class="op-hero__name">{{ profile.name || '运营人员' }}</div>
              <div class="op-hero__dept">{{ deptText || '运营' }}</div>
            </div>
            <span class="op-hero__role">{{ roleText || '运营' }}</span>
          </div>
          <div class="op-kpis">
            <div class="op-kpi" @click="openSub('message')">
              <div class="op-kpi__n">{{ unread.total }}<span class="op-kpi__dot" v-if="unread.total > 0"></span></div>
              <div class="op-kpi__l">未读消息</div>
            </div>
            <div class="op-kpi">
              <div class="op-kpi__n">{{ dash.room.service_todo || 0 }}</div>
              <div class="op-kpi__l">待店长确认</div>
            </div>
            <div class="op-kpi">
              <div class="op-kpi__n">{{ dash.room.today || 0 }}</div>
              <div class="op-kpi__l">今日到场人数</div>
            </div>
          </div>
        </div>

        <!-- 快捷入口（首项＝消息中心） -->
        <div class="op-quick">
          <div class="op-quick__hd"><span>快捷入口</span></div>
          <div class="op-grid">
            <div class="op-tile" v-for="q in quickItems" :key="q.key" @click="openSub(q.key)">
              <div class="op-tile__ic">
                <span class="op-tile__svg" v-html="q.icon"></span>
                <span class="op-tile__dot" v-if="q.key === 'message' && unread.total > 0">{{ unread.total > 99 ? '99+' : unread.total }}</span>
              </div>
              <div class="op-tile__label">{{ q.label }}</div>
            </div>
          </div>
          <div class="op-tip">数据为全公司口径；会议室订单支持【推送待办】与【完成服务】</div>
        </div>

        <!-- 【P196】账号：退出登录
             ★ 与销售端 client.vue 的「账号」区块 1:1 对齐（同一套 .acct-* 结构与视觉）：
               .op-quick__hd / .acct-list / .acct-row（图标 + 文案 + 箭头）
             ★ 运营端此前无任何退出入口（onLogout 仅挂在「权限不足态」），本次补齐。
             ★ 仅出现在「快捷入口」Tab（tabIndex===5）内，与用户要求一致。 -->
        <div class="op-quick op-quick--acct">
          <div class="op-quick__hd op-quick__hd--plain"><span>账号</span></div>
          <div class="acct-list">
            <div class="acct-row" @click="askLogout">
              <span class="acct-row__ic">
                <svg viewBox="0 0 24 24">
                  <path d="M15 4h3.5v16H15" />
                  <path d="M10.5 8 6.5 12l4 4" />
                  <path d="M6.5 12H15" />
                </svg>
              </span>
              <span class="acct-row__txt">
                <span class="acct-row__label">退出登录</span>
                <span class="acct-row__sub">退出后需重新登录</span>
              </span>
              <van-icon name="arrow" class="acct-row__arrow" />
            </div>
          </div>
        </div>
        </van-pull-refresh>
      </template>

      <!-- ============================================================
           【P239 修复】会议室订单 / 增值订单 两个 Tab 的模板被误删
             （P237 重写消息页时，替换区间从消息页一直吃到采购单之前，
              把 tabIndex===1 / ===2 两段一起删掉了 ⇒ 底部第 2、3 项点不动、列表空白）
             组件 import / components 注册都还在，只是模板没了 ⇒ 这里按「内联 Tab」口径补回。
             ⚠️ 判据用 tabIndex（不是 P183 备份里的 subKey）—— P224 起所有 Tab 都是内联页。
           ============================================================ -->
      <bill-list
        v-if="!subKey && tabIndex === 1"
        ref="billList"
        :key="'room-' + subSeq"
        bill-type="room"
        scope="all"
        api-prefix="operation"
        :ops-mode="true"
        :mask-amount="true"
        :readonly="true"
        :allow-edit="false"
        :show-back="false"
        filter-engine="sales"
        title="会议室订单"
        @back="closeSub"
        @pushtodo="onPushTodo"
        @finish="onFinish"
      />
      <bill-list
        v-if="!subKey && tabIndex === 2"
        ref="billList"
        :key="'value-' + subSeq"
        bill-type="value"
        scope="all"
        api-prefix="operation"
        :mask-amount="true"
        :readonly="true"
        :allow-edit="false"
        :show-back="false"
        title="增值订单"
        @back="closeSub"
      />

      <!-- ============================================================
           C. Tab 页：消息中心
              【P224】由「二级子页」改为「内联 Tab 页」（Tab4）—— 与房态/会议室/增值/采购一致：
                ① 底部 Tab 栏不再被 v-if 隐藏，用户可随时切走
                ② 去掉顶部返回箭头（Tab 栏本身就是出口）
                ③ 快捷入口点「消息中心」→ openSub('message') 改为切到本 Tab
           ============================================================ -->
      <!-- 【P235 修正】消息中心改回「subKey 子页」判据。
           原先 P233 把它写成 tabIndex===4，但 TABS 只有 5 项（0~4，4 是「快捷入口」），
           两页判据撞车 ⇒ 底部 Tab 第 5 项点不动、进消息后快捷入口再也回不去。
           现口径：subKey==='message' 渲染消息页；底部 Tab 栏在消息页**继续显示**（已去掉 !subKey 条件），
           顶部加返回箭头，用户可随时点 Tab 或箭头离开。 -->
      <!-- 【P237 UI 重设计】消息中心
           · 分类从「全部/待办/抄送」改为「待执行 / 抄送」两类（用户工单 1-2）
           · 待执行区：全选 + 批量「确认执行」；已确认的消息**不再出现**（后端按 service_status 过滤）
           · 视觉：去掉蓝条与多余嵌套，改为「标签 + 时间 + 正文」的单层卡片，信息层级更清晰 -->
      <!-- 【P244】消息中心 → 改用全平台共用组件 MessagePanel
           三 Tab（未读 / 待处理 / 全部）+ 红色气泡角标 + 描述完整展示；
           「已确认」并入「全部」Tab（用户 10-07 确认）。 -->
      <!-- 【P249】按全平台统一组件接口传参：
           · tabs 由本页算好（含角标数），顺序：未读 / 待处理 / 全部
           · 批量：**仅「待处理」Tab 开启**，动作 = confirm（保留批量确认）
           · show-action：卡片底部「去处理」，点击进会议室订单详情 -->
      <!-- 【P304】★★ 修「向下滚动分页失效 / 不能滑动」
           ⚠️ 这层包裹容器是修复的**另一半**，与组件侧 van-list 缺一不可。
           根因（CDP 实测几何，非推测）：
             #app / body / html 被 lib.min.css 设成 height:100% + overflow:hidden；
             .op-page 自己是 min-height:100vh + overflow:visible
             ⇒ 内容撑到 2488px，但**整条链没有任何滚动容器**
             ⇒ van-list 找不到可滚祖先，永不上报触底；页面也滑不动。
           做法（与财务端 P296 / 销售端完全同口径）：
             .op-msgwrap 自己 flex 撑高、overflow:hidden；
             内部的 .van-pull-refresh 成为**唯一滚动容器**（overflow-y:auto），
             Vant 的 getScroller 从元素自身向上找，命中自己 ⇒ scrollTop 可变、可滑动。
           只包消息页，不影响房态/订单/采购各 Tab。 -->
      <!-- 【P308】恢复运营端「待处理 → 批量『我知道』」历史功能（P232/P281 messageConfirmBatch）。
           体现为模板上的 :allow-pending-batch="true"（组件默认 false，销售/市场/财务端仍按 P301 口径禁止）。
           ★ 教训：组件标签**内部**（属性之间）不能放 HTML 注释，
             vue-loader 会把注释当纯文本节点渲染到页面上（实测页面上直接显示属性源码）。 -->
      <div class="op-msgwrap" v-if="subKey === 'message'"
           :style="{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }">
      <message-panel
        title="消息中心"
        :tabs="msgTabsView"
        :tab="msgTab"
        :list="msgRows"
        :loading="msgLoading"
        :refreshing="msgRefreshing"
        :paged="true"
        :finished="msgFinished"
        :list-loading="msgListLoading"
        :busy="msgConfirming"
        :err="msgErr"
        :empty-text="msgEmptyTextForTab()"
        :batch-enabled="true"
        :is-core-role="msgIsCoreRole"
        :allow-pending-batch="true"
        :batch-action="batchActionForTab()"
        :show-detail="msgTab === 'pending' || msgTab === 'reject'"
        :show-action="true"
        action-text="去处理"
        @back="closeSub"
        @tab="onMsgTab"
        @refresh="loadMessages(true)"
        @load-more="onMsgLoadMore"
        @open="openMessage"
        @action="openMessage"
        @batch="onMsgBatch"
      />
      </div>

      <div v-if="!subKey && tabIndex === 3" class="op-sub">
        <div class="customer-header" ref="subHead">
          <div class="customer-header__top">
            <div class="customer-header__left">
              <span class="customer-header__title">采购单</span>
            </div>
            <div class="customer-header__search">
              <el-input v-model="purKw" class="search-input" placeholder="搜索名称/编号" @change="loadPurchase(true)">
                <i slot="prefix" class="el-icon-search"></i>
              </el-input>
            </div>
          </div>
        </div>
        <div class="customer-header-spacer" :style="{ height: headH + 'px' }"></div>
        <div class="bill-sum">
          <span>共 {{ purTotal }} 条</span>
        </div>
        <van-pull-refresh v-model="purRefreshing" @refresh="loadPurchase(true)">
          <div class="list-box">
            <div class="crm-item" v-for="r in purRows" :key="r.sn">
              <div class="item-head customer-card">
                <div class="customer-card__main">
                  <div class="customer-card__content">
                    <div class="customer-card__header">
                      <div class="customer-card__name-wrap">
                        <span class="customer-card__name">{{ r.name || r.finance_no || '采购单' }}</span>
                      </div>
                      <span class="status-tag tag-allot" v-if="r.stage_name">{{ r.stage_name }}</span>
                    </div>
                    <div class="customer-card__row">编号：{{ r.finance_no || '-' }} · 签订：{{ r.sign_date || '-' }}</div>
                    <div class="customer-card__row customer-card__row--muted">创建：{{ r.create_user_name || '-' }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bill-empty" v-if="!purLoading && !purRows.length">
              <div class="bill-empty__icon">📦</div>
              <div class="bill-empty__text">暂无采购单</div>
              <div class="bill-empty__hint">口径：财务合同中 is_purchase=1 的记录（当前数据集中尚无此类单据）</div>
            </div>
          </div>
        </van-pull-refresh>
      </div>

      <!-- ============================================================
           F. Tab 页：房态（【P169】1:1 对齐销售端 client.vue 的房态网格 UI）
              日期区间 header + 门店横滚 + 会议室×日期 四色格子 + 只读详情弹窗
           【P176】由「子页」改为「内联 Tab 页」：
             ① 底部 Tab 栏保持可见（不再因 subKey 被 v-if 隐藏）⇒ 用户可随时切走，解决「出不来」
             ② 进入运营端默认落在此页（tabIndex 默认 0）⇒ 与销售端首屏一致
             ③ 不再需要单独的返回条（Tab 栏本身就是出口），返回条仅保留在 no-tabbar 场景
           ============================================================ -->
      <div v-if="!subKey && tabIndex === 0" class="op-tabpage">
        <div class="room-status-page">
          <div class="room-status-board">

            <div class="header" @click="handleDialogDate">
              <div class="header__date">
                <van-icon name="calendar-o" class="header__cal" />
                <span class="header__range">{{ roomDateRangeValue[0] }} <i>~</i> {{ roomDateRangeValue[1] }}</span>
              </div>
              <div class="header__btn">更换日期 <van-icon name="arrow" /></div>
            </div>

            <!-- 【P181】账号条（P180 新增的「运营人名称 + 退出」）已按需求【整体移除】。
                 房态首屏不再显示姓名/角色/退出入口；退出仍可从「快捷入口」Tab 内触发。
                 历史：P180 因「房态首屏无退出出口」而新增，现用户明确要求去掉。 -->

            <div class="store-wrap">
              <div
                class="store-item"
                :class="{ active: storeSN == item.sn }"
                @click="searchStore(item.sn)"
                v-for="item in storesData"
                :key="item.sn"
              >{{ item.name }}</div>
            </div>

            <!-- 【P180】图例条已按需求【整体移除】。
                 历史：P176 新增（用户当时不知四色含义）；P178 修正过文案。
                 现用户明确要求「图示例部分去掉」⇒ 删除模板 + 对应 CSS + legendOpen 状态。
                 保留四色口径备查：3绿 已成交已收款 / 1蓝 已成交待收款 /
                 2黄 未成交有金额 / 4红 免费预留 / 灰虚线 未使用。 -->

            <div class="scroll-wrapper" :style="roomScrollH ? { height: roomScrollH + 'px' } : ''">
              <div class="scroll-content" :style="'width:' + tableContainerWith + 'px;'">

                <!-- 日期表头 -->
                <div class="date-header">
                  <div
                    class="date-item"
                    :class="{ today: item.isToday }"
                    v-for="item in roomData['dateRange']"
                    :key="item.date"
                  >{{ item.date }}<br>{{ item.weekday }}</div>
                </div>

                <!-- 【P183】★★ 四色口径改为与销售端 client.vue **逐字 1:1 同构**（按用户选定「完全跟随销售端」）：
                     1 蓝 = 有合同(contracts) 且 合同应收(receivables) > 0   —— 已成交待收款
                     2 黄 = 无合同 且 订单金额(amount) > 0                  —— 未成交有金额
                     3 绿 = 有合同 且 合同应收(receivables) <= 0            —— 已成交已收款
                     4 红 = 无合同 且 订单金额(amount) == 0                 —— 免费预留
                     ⚠️ 原 P169/P178 用的 `roomCellTone()`（判定 orders_main + is_payment）语义与此**正交**，
                        实测同一批 64 格：销售端全黄 / 运营端全绿，**0 格一致** ⇒ 已废弃，改直用销售端条件。 -->
                <div class="room-list">
                  <div class="room-row" v-for="(item, idx) in roomData['rooms']" :key="idx">
                    <div class="room-name">{{ item.room_number }}</div>
                    <div class="room-days">
                      <div class="day-cell" v-for="(item2, dIdx) in item['dateRange']" :key="dIdx">
                        <div v-for="(item3, index) in item2['dateArry']" :key="index">

                          <div class="status status--empty" v-if="!item3['order']">
                            <span class="status--empty__txt">未使用</span>
                          </div>

                          <!-- 【P183】1 蓝 = 有合同 且 应收 > 0（销售端 client.vue L69 同条件） -->
                          <div
                            @click="openRoomDetail(roomCellSn(item3))"
                            v-if="item3['order'] && item3['order']['contracts'] && item3['order']['contracts']['receivables'] > 0"
                            class="status content_status_1 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】2 黄 = 无合同 且 金额 > 0（销售端 client.vue L77 同条件） -->
                          <div
                            @click="openRoomDetail(roomCellSn(item3))"
                            v-if="item3['order'] && (!item3['order']['contracts']) && item3['order']['amount'] > 0"
                            class="status content_status_2 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】3 绿 = 有合同 且 应收 <= 0（销售端 client.vue L82 同条件） -->
                          <div
                            @click="openRoomDetail(roomCellSn(item3))"
                            v-if="item3['order'] && item3['order']['contracts'] && item3['order']['contracts']['receivables'] <= 0"
                            class="status content_status_3 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】4 红 = 无合同 且 金额 == 0（销售端 client.vue L88 同条件） -->
                          <div
                            @click="openRoomDetail(roomCellSn(item3))"
                            v-if="item3['order'] && (!item3['order']['contracts']) && item3['order']['amount'] == 0"
                            class="status content_status_4 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="bill-empty" v-if="!roomLoading && !roomData['rooms']">
                    <div class="bill-empty__icon">🏠</div>
                    <div class="bill-empty__text">该门店暂无会议室</div>
                  </div>
                </div>

              </div>
            </div>

            <!-- 【P170】日期搜索：1:1 移植销售端 client.vue（选择时间对话框 + 日期选择器） -->
            <van-dialog v-model="dialogDatePop" title="选择时间" show-cancel-button @confirm="searchConfirm">
              <div class="dialogDate">
                <div class="dialogDateRow" @click="selectDate(0)"> <span class="dialogDateLable">开始时间</span> <span class="dialogDateValue">{{ beignDate }}</span></div>
                <div class="dialogDateRow" @click="selectDate(1)"> <span class="dialogDateLable">结束时间</span> <span class="dialogDateValue">{{ endDate }}</span></div>
              </div>
            </van-dialog>

            <van-popup v-model="dialogDate" round position="bottom" :style="{ height: '50%' }">
              <van-datetime-picker
                @confirm="dateConfirm"
                @cancel="dateCancel"
                v-model="currentDate"
                type="date"
              />
            </van-popup>

            <!-- ===== 只读详情弹窗（1:1 对齐销售端 detail-popup，去掉「编辑」入口） ===== -->
            <van-popup v-model="dialogDetail" round position="bottom" class="detail-popup" :style="{ height: '82%' }">
              <div class="detail-sheet">
                <div class="detail-sheet__hd">
                  <div class="detail-sheet__title">会议室订单详情</div>
                  <van-icon name="cross" class="detail-sheet__close" @click="closeDetail" />
                </div>

                <div class="detail-sheet__body">
                  <!-- 客户 / 销售 概览 -->
                  <div class="detail-hero">
                    <div class="detail-hero__main">
                      <div class="detail-hero__name">{{ orderDetailData['customers'] || '未知客户' }}</div>
                      <div class="detail-hero__sub">销售负责人：{{ orderDetailData['staff'] || '-' }}</div>
                    </div>
                    <!-- 【P339-UI】状态标签区：独立成行 + 可换行。
                         原与客户名同一行 flex，3 个标签 flex-shrink:0 把客户名压到 ~40px
                         ⇒ 中文逐字竖排「智者四海（北京）技术有限公司」；且
                         detailSvcText(service_status=2) 与 detailStageTextFull(stage=2)
                         都渲染成「店长已确认」⇒ 同一行出现两个相同标签。
                         现：① 标签移到独立行 flex-wrap；② 两个维度文案去重（相同只显示一次）。 -->
                    <div class="detail-hero__tags">
                      <span class="op-svc" :class="'op-svc--' + detailSvc">{{ detailSvcText }}</span>
                      <span v-if="detailStageTextFull !== detailSvcText" class="op-svc op-stage-chip"
                            :class="'op-stage-chip--' + detailStage">{{ detailStageTextFull }}</span>
                      <span v-if="detailContractStage !== null" class="op-svc op-stage-chip" :class="'op-stage-chip--con'"
                            :title="'关联财务合同：' + detailContractStageText">合同{{ detailContractStageText || '—' }}</span>
                    </div>
                  </div>

                  <!-- 常用联系人 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>常用联系人</div>
                    <div v-if="orderDetailData['contact'] && orderDetailData['contact'].length" class="detail-cards">
                      <div class="detail-card" v-for="(c, ci) in orderDetailData['contact']" :key="ci">
                        <div class="detail-card__row"><span class="detail-card__label">姓名</span><span class="detail-card__val">{{ c.name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">手机</span><span class="detail-card__val">{{ c.phone }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">微信</span><span class="detail-card__val">{{ c.wx }}</span></div>
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无联系人</div>
                  </div>

                  <!-- 会议室预定信息 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>会议室预定信息</div>
                    <div v-if="orderDetailData['orders_meeting_room'] && orderDetailData['orders_meeting_room'].length" class="detail-cards">
                      <div class="detail-card" v-for="(m, mi) in orderDetailData['orders_meeting_room']" :key="mi">
                        <div class="detail-card__head">{{ m.meeting_room_name }} <em>{{ m.meeting_room_room_number }}</em></div>
                        <div class="detail-card__row"><span class="detail-card__label">类型</span><span class="detail-card__val">{{ m.order_type_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">桌型</span><span class="detail-card__val">{{ m.table_types }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">设备</span><span class="detail-card__val">{{ m.option_service }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">日期</span><span class="detail-card__val">{{ m.on_site_time }}</span></div>
                        <!-- 【P312 需求002】时间类型 / 时段 拆成两行独立字段（PC 是两个字段，H5 原挤在一行） -->
                        <div class="detail-card__row"><span class="detail-card__label">时间类型</span><span class="detail-card__val">{{ m.time_type_name || '—' }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">时段</span><span class="detail-card__val">{{ m.start_time || '—' }}</span></div>
                        <!-- 【P312 需求002】人数：后端已下发 number_people，此前前端未渲染 -->
                        <div class="detail-card__row"><span class="detail-card__label">人数</span><span class="detail-card__val">{{ m.number_people || '—' }}</span></div>
                        <!-- 【P442】销售价格：运营端仅「运营条线部门负责人」可见。
                             后端 client/getOrderDetail 已按身份剔除 amount ⇒ 此处只判字段是否存在，
                             无权限者整行不渲染（不留 *** 占位）。 -->
                        <div class="detail-card__row" v-if="m.amount !== undefined && m.amount !== null && m.amount !== ''"><span class="detail-card__label">销售价格</span><span class="detail-card__val">¥{{ m.amount }}</span></div>
                        <!-- 【P343-UI】★「实际服务人数 / 服务备注」并入本卡片（终态 svc>=4 才显示）。
                             原先它们挂在弹层底部 `.op-svc-done`，与「完成（填写服务人数）」按钮
                             挤在同一行、互相重叠，且样式单薄很难看。
                             现按业务属性归位到「会议室预定信息」卡片尾部，与类型/桌型/时段同列。 -->
                        <template v-if="detailSvc >= 4 || detailStage >= 4">
                          <div class="detail-card__row detail-card__row--done">
                            <span class="detail-card__label">实际服务</span>
                            <span class="detail-card__val">
                              <span class="op-done-tag">已完成</span>
                              <span v-if="detailHeadcount" class="op-done-num">{{ detailHeadcount }} 人</span>
                              <span v-else class="detail-card__val--muted">—</span>
                            </span>
                          </div>
                          <div class="detail-card__row" v-if="detailFinishRemark">
                            <span class="detail-card__label">服务备注</span>
                            <span class="detail-card__val">{{ detailFinishRemark }}</span>
                          </div>
                        </template>
                        <!-- 【P312 需求002】备注（富文本）：v-html 渲染；运营端金额脱敏，备注不含金额故照常展示 -->
                        <div class="detail-card__row" v-if="m.remark">
                          <span class="detail-card__label">备注</span>
                          <span class="detail-card__val detail-rich" v-html="m.remark"></span>
                        </div>
                        <!-- 【P169-B】金额脱敏：回款计划整组不展示 -->
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无预定信息</div>
                  </div>

                  <!-- 增值业务 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>增值业务</div>
                    <div v-if="orderDetailData['added_services'] && orderDetailData['added_services'].length" class="detail-cards">
                      <div class="detail-card detail-card--sm" v-for="(a, ai) in orderDetailData['added_services']" :key="ai">
                        <div class="detail-card__row"><span class="detail-card__label">时间</span><span class="detail-card__val">{{ a.on_date }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">类型</span><span class="detail-card__val">{{ a.value_added_services_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">名称</span><span class="detail-card__val">{{ a.value_added_services_goods_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">数量</span><span class="detail-card__val">{{ a.quantity }}</span></div>
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无增值业务</div>
                  </div>
                </div>

                <!-- 【P93】运营动作位
                     【P232/P234】★ 去掉「推送待办」，改为「确认(我知道了)」。
                       业务原因：待办由系统自动推送（销售端改单 P230 / PC端改单 P231 /
                       财务审批通过 P229），运营再手动「推送」会造成重复打扰；
                       运营的职责是**确认知悉**，确认后知会运营部门负责人（P401：由逐级上行收敛为负责人单人）。
                       阶段语义：0未推送 → 1待确认 → 2已确认 → 3已完成。
                       【P234 修正】原先 svc=0 只显示「待推送(disabled)」，
                       而消息列表最新一条常常就是 svc=0 的变更单 ⇒ 用户看到「没有确认按钮」
                       误以为功能没开发。现改为 **svc 0/1 都可确认**（幂等，后端会判已确认）。 -->
<!-- 【P282/P339】操作区判据统一（此前 bug：外层按 detailSvc(service_status)、
       内层「完成服务」按 detailStage，两套字段打架 ⇒ svc<3 且 stage>=3 时
       进入分支却只能点置灰按钮，「完成服务」永远点不动，用户观感＝点了没反应）。
       【P339 修正】**以 stage 为唯一进度判据**（service_status 是「待办推送态」，
       与业务进度不同源，混用必然自相矛盾）：
         stage 1   ⇒ 「确认(我知道了)」+「完成服务」都可点
         stage 2   ⇒ 确认按钮置灰，保留「完成服务」
         stage>=3 ⇒ 展示「服务已完成」+ 终态操作（填写服务人数 / 标记完成） -->
                <!-- 【P405】★★ 未产生支付合同 ⇒ 「确认(我知道了)」「完成服务」都不出现。
                     判据由后端 has_paid_contract 下发（与规则 00「未支付/无合同 → 不通知运营」同源）：
                     没有支付就没有待办，运营也就没有可确认 / 可完成的动作对象。
                     detailPaidContract === null（未加载 / 老后端）时按「已支付」渲染，
                     只在后端**明确**说 false 时才整块隐藏。 -->
                <div
                  class="op-svc-deny"
                  v-if="detailPaidContract === false"
                >该订单未产生支付合同，无需确认 / 完成服务</div>

                <template v-else>
                  <!-- 【P401】★★ 「确认(我知道了)」按后端下发的 can_confirm 决定**是否渲染**。
                       用户 10-20 决策：「不放开权限，前端隐藏按钮」。
                       原实现对非订单负责人（如运营负责人 480 打开 476 的单）照样渲染按钮，
                       点了被 confirmTodo 的 ownsRoomOrder 拦下 ⇒ 用户只看到「报错被拦截」。
                       现在：can_confirm=false ⇒ **不渲染按钮**，只给一行原因说明。
                       ★ detailCanConfirm === null（详情接口未返回）时也不渲染，
                         避免「先亮后灭」的闪烁和误点。 -->
                  <div
                    class="op-svc-deny"
                    v-if="detailStage < 2 && detailCanConfirm === false && detailCanConfirmReason"
                  >{{ detailCanConfirmReason }}</div>
                  <div class="op-svc-ft" v-if="detailStage < 3">
                    <van-button
                      v-if="detailStage < 2 && detailCanConfirm === true"
                      block type="warning" size="small"
                      :loading="msgConfirming"
                      @click="confirmFromDetail"
                    >确认(我知道了)</van-button>
                    <van-button
                      v-else-if="detailStage >= 2"
                      block plain type="warning" size="small" disabled
                    >已确认（我知道了）</van-button>
                    <!-- 【P405】「完成服务」改由后端 can_finish_service 决定可见性
                         （非 owner / 未产生支付合同 / 已到终态 都不再渲染） -->
                    <van-button
                      v-if="detailCanFinish !== false"
                      block type="primary" size="small"
                      :loading="finishSubmitting"
                      @click="finishFromDetail"
                    >完成服务</van-button>
                    <div
                      class="op-svc-deny"
                      v-else-if="detailFinishReason"
                    >{{ detailFinishReason }}</div>
                  </div>
                  <!-- 【P339】stage>=3：进入终态区。「完成（填写服务人数）」在 stage>=3 时恒可点，
                       openCompleteDialog 的前置校验改为读 stage，不再被 svc 卡住。 -->
                  <!-- 【P404】★ 已终态（service_status=4）后必须收掉按钮。
                       根因：completeService 只把 service_status 推到 4，stage 仍停在 3，
                       而本区判据是 `v-else` of `detailStage < 3` ⇒ 完成后按钮**永远常驻**，
                       再点只会拿到「该订单已完成（幂等跳过）」⇒ 用户观感＝「点完还让我再填一次」。
                       现按 service_status 兜一层：svc>=4 直接显示置灰的已完成态。 -->
                  <div class="op-svc-ft" v-else>
                    <van-button
                      v-if="detailSvc < 4"
                      block type="primary" size="small"
                      :loading="completeSubmitting"
                      @click="openCompleteDialog"
                    >完成（填写服务人数）</van-button>
                    <!-- 【P411】★ 已终态不再置灰锁死：用户 10-21 需求「这个功能可以一直修改，
                         目前是一次填写后不能再修改」。改为常驻可点的「修改服务人数」，
                         后端 completeService 已支持反复覆盖。 -->
                    <van-button
                      v-else
                      block plain type="primary" size="small"
                      :loading="completeSubmitting"
                      @click="openCompleteDialog"
                    >修改服务人数 / 备注</van-button>
                  </div>
                </template>
                <!-- 【P343-UI】原 `.op-svc-done`（「订单已完成 / 服务人数 / 备注」）已移除：
                     它与上方「完成（填写服务人数）」按钮挤在同一行、互相重叠（用户 10-14 反馈），
                     现把「实际服务人数 / 服务备注」并入「会议室预定信息」卡片尾部（见上方 detail-card__row--done）。 -->
              </div>
            </van-popup>

          </div>
        </div>
      </div>
    </template>

    <!-- ===== 底部 Tab（【P235 修正】TABS 实际只有 5 项 → 索引 0~4：
         0房态 1会议室订单 2增值订单 3采购单 4快捷入口；
         消息中心**不再占 Tab 索引**（P233 曾错设 tabIndex===4，与「快捷入口」撞车 ⇒
         底部 Tab 第 5 项点不动、进消息页后「快捷入口」永远显示不出来）。
         消息页改回 subKey 子页 + 底部 Tab 栏继续显示（见下方 v-if）+ 顶部返回箭头，
         这样既不撞索引，用户也随时能点 Tab 切走。 ===== -->
    <van-tabbar v-if="!authDenied" v-model="tabIndex" active-color="#2b5fd9" inactive-color="#969799">
      <van-tabbar-item v-for="(t, i) in tabs" :key="t.key" :icon="t.icon" :badge="t.key === 'quick' && unread.total > 0 ? (unread.total > 99 ? '99+' : String(unread.total)) : ''" @click="onTabClick(i)">{{ t.label }}</van-tabbar-item>
    </van-tabbar>

    <!-- 【P196】退出确认弹窗：复用全站统一 ConfirmDialog（与销售端 client.vue P46 同一套）
         【P181】曾随房态账号条一并移除的旧弹窗已由本次「快捷入口 → 账号 → 退出登录」替代。 -->
    <ConfirmDialog
      v-model="logoutShow"
      title="退出登录"
      desc="确认退出当前账号？退出后需重新登录才能继续使用。"
      confirm-text="退出"
      @confirm="doLogout"
    />

    <!-- 【P281】运营动作确认（推送待办 / 完成服务）—— 替换原会报错的 $dialog
         【P340 BUG 修复】★title 必须用**静态属性**，不能用 `:title="opDlgTitle"`：
         实测（Playwright + 真实运营 token）绑定 `opDlgShow` 的这个 ConfirmDialog
         **组件实例从未挂载**（实例 uid 14/16 存在、uid 15 缺失），无论 `opDlgShow`
         设 true、还是 `$set`、还是初始值直接给 true，DOM 里都查不到 `.confirm-dialog`
         ⇒ 点「完成服务 / 推送待办」毫无反应。
         对照组：同位置的 logoutShow / msgBatchShow 两个都正常挂载，**唯一差别是它们
         用静态 title**。故这里改成静态 title，动作名放到 desc 首行。 -->
    <ConfirmDialog
      v-model="opDlgShow"
      title="请确认操作"
      :desc="opDlgDesc"
      confirm-text="确定"
      @confirm="opDlgConfirm"
      @cancel="opDlgCancel"
    />

    <!-- 【P281】消息批量操作二次确认：按 Tab 分别文案（已读 / 我知道了 / 删除） -->
    <ConfirmDialog
      v-model="msgBatchShow"
      title="批量操作"
      :desc="msgBatchTip"
      :confirm-text="batchTextForTab() || '确定'"
      @confirm="onMsgBatchConfirm"
      @cancel="onMsgBatchCancel"
    />

    <!-- 【新功能】运营端：标记订单完成（state=4）—— 填写服务人数 + 备注 -->
    <van-popup v-model="completeShow" round position="bottom" class="complete-popup">
      <div class="complete-popup__hd">
        <div class="complete-popup__title">{{ completeEditMode ? '修改服务人数' : '标记订单完成' }}</div>
        <div class="complete-popup__sub">
          {{ completeEditMode
             ? '该订单已完成，仍可反复修改服务人数与备注，保存后立即生效（不会重复通知管理者）。'
             : '请填写本次服务的实际人数与备注，提交后状态将置为「已完成」并通知管理者。' }}
        </div>
      </div>
      <div class="complete-popup__bd">
        <van-field
          v-model="completeHeadcount"
          type="digit"
          label="服务人数"
          placeholder="请输入服务人数"
          input-align="right"
        />
        <van-field
          v-model="completeRemark"
          type="textarea"
          rows="3"
          autosize
          label="备注"
          placeholder="可填写本次服务备注（选填）"
        />
      </div>
      <div class="complete-popup__ft">
        <van-button block plain type="default" size="small" @click="completeShow = false">取消</van-button>
        <van-button block type="primary" size="small" :loading="completeSubmitting" @click="submitComplete">{{ completeEditMode ? '保存修改' : '完成' }}</van-button>
      </div>
    </van-popup>
    </template>
  </div>
</template>

<script>
import {
  operationMyProfile,
  operationDashboard,
  operationRoomStatus,
  operationGetRoomDate,
  operationGetStores,
  operationGetRooms,
  operationPurchaseList,
  operationPurchaseSum,
  operationMessageList,
  operationMessageSummary,
  operationMessageRead,
  operationMessageReadAll,
  operationMessageUnread,
  operationRoomTodoPush,
  operationRoomFinishService,
  /* 【新功能】终态：标记完成（服务人数 + 备注） */
  operationRoomComplete,
  /* 【P232】确认待办（我知道了）→ 知会运营部门负责人（P401 文案口径） */
  operationRoomTodoConfirm,
  /* 【P281】消息批量三件套：确认 / 删除 / 已读
   * 【P400】★ 确认改走 operationMessageTodoConfirmBatch（订单维度，等价「我知道了」）；
   *   operationMessageConfirmBatch 是消息维度简化版（缺 stage/审批单/上级通知），
   *   运营端【待处理】已不再使用，保留 import 仅作历史对照会触发 no-unused-vars，
   *   故从本文件移除。 */
  operationMessageTodoConfirmBatch,
  operationMessageDeleteBatch,
  operationMessageReadBatch,
  /* 【P284】房态订单详情（syncDetailSvc 用它刷新 service_status）。
     ★ 此前漏 import ⇒ 打开详情直接 ReferenceError。 */
  operationRoomOrderDetail
} from '@/api/api.js'
import { getClientOrderDetailData } from '@/api/api.js'
import { getProfile, logout, isLoggedIn } from '@/util/clientAuth.js'
import crmWs from '@/util/websocket.js'
import { suppressGlobalError } from '@/util/request.js'
import BillList from '@/components/BillList.vue'
import MessagePanel from '@/components/MessagePanel.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
/* 【P314】运营端登录层：与市场端（P120）同一套范式 —— 未登录时**内联渲染**登录组件，不跳转 */
import ClientLogin from '@/components/ClientLogin.vue'
import PcLogin from '@/components/PcLogin.vue'

function svg(inner, size) {
  const n = size || 23
  return '<svg viewBox="0 0 24 24" width="' + n + '" height="' + n + '" fill="none" stroke="currentColor"'
    + ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + inner + '</svg>'
}
function money(v) {
  const n = Number(v || 0)
  return '¥' + n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/* 【P176】底部 Tab 定义：Tab0 由「首页」改为「房态」，与销售端 client.vue 首 Tab 一致。
   原首页（画像 + KPI + 快捷九宫格）内容并入「快捷入口」Tab（index 4）。
   【P224】新增 Tab5「消息」：把消息中心从「二级子页」也改为「内联 Tab 页」。
   原因：它是唯一没跟上 Tab 化的入口 —— 仍走 subKey==='message' 子页，
        而底部 Tab 栏是 `v-if="!authDenied && !subKey"` ⇒ 一进消息页 Tab 栏整个消失，
        只能靠顶部返回箭头退出，正是 P176 要消灭的「进得去出不来」。 */
/* 【P231】★ 消息中心从底部 Tab 中去掉。
   原因：消息是「待办提醒」而非主功能区，用户要求只保留快捷入口进入（与销售端一致）。
   ★ 消息页仍保留为内联 Tab（index 4），由 openSub('message') 切到，
     底部 Tab 栏不再显示「消息」这一项 ⇒ TABS 去掉 message 项，
     「快捷入口」index 由 5 变 4，模板中 tabIndex===5 的判据同步改为 ===4。 */
const TABS = [
  { key: 'room_status', label: '房态', icon: 'wap-home-o' },
  { key: 'room_order', label: '会议室订单', icon: 'shop-o' },
  { key: 'value_order', label: '增值订单', icon: 'gift-o' },
  { key: 'purchase', label: '采购单', icon: 'shopping-cart-o' },
  { key: 'quick', label: '快捷入口', icon: 'apps-o' }
]

/* Tab 索引 → 子页 key
   【P176】0 → 房态：★ 必须为 ''，房态作为「内联 Tab 页」渲染（见模板 tabIndex===0），
           不能当子页 —— 子页会因 v-if="!subKey" 把底部 Tab 栏整个隐藏掉，
           用户就会「进得去出不来」，这正是本次要修的问题。
           1/2/3 → 各自子页；4 → ''（快捷入口，渲染并入的原首页内容）
   【P224】★ 全部 6 个 Tab 均为内联页 ⇒ 一律为 ''。
           保留本映射表结构：历史遗留的 openSub/quickItems 仍按 key 切 Tab，
           未来若再引入真子页，只需在此登记即可，不必改模板。 */
const TAB_SUB = { 0: '', 1: 'room_order', 2: 'value_order', 3: 'purchase', 4: '', 5: '' }

const SCENE_TEXT = {
  room_order_todo: '会议室待办',
  room_order_cc: '逐级抄送',
  room_order_finish: '服务完成'
}

export default {
  name: 'OperationHome',
  components: { BillList, MessagePanel, ConfirmDialog, ClientLogin, PcLogin },
  data() {
    return {
      /* 【P314】登录层状态：未登录时停在 #/operation 直接显示登录页，不再跳 #/login */
      showLogin: false,
      innerWidth: (typeof window !== 'undefined' ? window.innerWidth : 0),
      authDenied: false,
      authMsg: '',
      profile: {},
      unread: { primary: 0, cc: 0, total: 0 },
      dash: { room: {}, value: {}, purchase: {} },
      tabIndex: 0,
      /* 【P112】首页（含快捷入口）下拉刷新 */
      homeRefreshing: false,
      subKey: '',
      subSeq: 0,
      /** 【P93】当前子页固定头部实测高度 → spacer 占位（否则内容被 position:fixed 头部遮住） */
      headH: 0,
      tabs: TABS,
      /* 消息中心 */
      msgRows: [],
      msgTotal: 0,
      /* 【P240】默认落在「待确认」分类 */
      msgTab: 'pending',
      /* 【P240】四类计数（待确认/已确认/未读/抄送），由 messageSummary 拉取 */
      msgCounts: { unread: 0, pending: 0, reject: 0 },
      /* 【P301】后端下发的「四大岗位」能力位（null=还没拿到，先按 true 兜底） */
      msgIsCoreRoleRaw: null,
      msgLoading: false,
      msgRefreshing: false,
      /* 【P304】上拉分页状态（MessagePanel paged=true 时驱动 van-list）
         msgPage      当前页码，从 1 起
         msgPageSize  每页条数
         msgListLoading  ★独立于 msgLoading：van-list 是 v-model:loading 双向状态机，
                          与 pull-refresh 的 msgLoading 共用会互相打断导致卡死
         msgFinished  是否到底（本次返回条数 < pageSize 即到底） */
      msgPage: 1,
      msgPageSize: 20,
      msgListLoading: false,
      msgFinished: false,
      /* 【P297】消息请求的超时兜底定时器：请求迟迟不回来也能让「加载中」结束 */
      _msgFinally: null,
      /* 【P232-fix】消息列表加载失败原因（渲染到页面，便于线上自查） */
      msgErr: '',
      /* 【P232】批量确认：勾选中的消息 sn + 确认中态 */
      msgPicked: [],
      msgConfirming: false,
      /* 【P281】批量操作二次确认（P196 自研 ConfirmDialog 驱动）
       *  ★ 必须在 data 里声明：Vue2 只对data 里已声明的属性做响应式，
       *   直接在 onMsgBatch 里赋值 this.msgBatchShow 不会触发视图更新。 */
      msgBatchShow: false,
      /* 【P281】运营动作（推送待办 / 完成服务）的确认弹窗状态 */
      opDlgShow: false,
      opDlgTitle: '',
      opDlgDesc: '',
      opDlgFn: null,
      msgBatchTip: '',
      _msgBatchSns: [],
      _msgBatchRun: null,
      /* 采购单 */
      purRows: [],
      purTotal: 0,
      purSum: 0,
      purKw: '',
      purLoading: false,
      purRefreshing: false,
      /* 房态（【P169】与销售端 client.vue 同构：日期区间 + 门店横滚 + 会议室×日期 网格） */
      storesData: [],
      storeSN: '',
      roomDateRangeValue: [],
      roomCurDate: {},
      roomData: {},
      tableContainerWith: 0,
      roomLoading: false,
      /* 【P180】房态滚动区动态高度
         （原 P176 的 legendOpen 图例折叠态已随图例条整体移除）
         【P181】原 P180 的 logoutShow（账号条退出弹窗）已随账号条一并移除
         【P196】logoutShow 恢复：退出入口迁到「快捷入口 → 账号」，弹窗复用 ConfirmDialog */
      logoutShow: false,
      roomScrollH: '',
      /* 【P170】日期搜索：与销售端 client.vue 同构（原运营端缺失 → 「更换日期」不可用） */
      dialogDate: false,
      dialogDatePop: false,
      dialogDateType: 0,
      currentDate: new Date(),
      beignDate: '',
      endDate: '',
      /* 只读详情弹窗 */
      dialogDetail: false,
      orderDetailData: {},
      currentDetailSn: '',
      detailSvc: 0,
      /* 【P335】stage：业务进度维度（与 detailSvc/service_status 两套并存展示）
       *  1待执行 2店长已确认(我知道了) 3服务已完成 4终态 */
      detailStage: 1,
      detailStageText: '待执行',
      /* 【P401】「当前登录人能否点『我知道了』」，由后端 roomOrderDetail 下发：
       *   null=未加载（隐藏按钮，避免非 owner 看到一闪而过的可点按钮）
       *   true=可点（owner 本人且 service_status<2）
       *   false=不可点（非 owner / 已确认）→ 按钮不渲染，只展示 detailCanConfirmReason
       * ★ 权限本身**没有放开**（后端 confirmTodo 仍只认 owner_id），
       *   这里只是把「点了才报被拦截」改成「不给你点 + 说明原因」。
       * ★ H5 拿不到 staffId，判据只能由后端算好下发。 */
      detailCanConfirm: null,
      detailCanConfirmReason: '',
      /* 【P405】★ 未产生支付合同的会议室订单，不出现「我知道了」「服务完成」按钮。
       *   detailPaidContract: null=未加载 / true=已产生支付合同 / false=没有 → 整块动作区隐藏
       *   detailCanFinish   : null=未加载 / true=可点「完成服务」/ false=不可点（非 owner、
       *                       已到终态、或未产生支付合同）→ 按钮不渲染，只展示 reason
       * ★ 判据全部由后端 roomOrderDetail 算好下发（与规则 00「未支付不通知运营」同源），
       *   前端只做「显不显示」，绝不自己推断支付状态。 */
      detailPaidContract: null,
      detailCanFinish: null,
      detailFinishReason: '',
      /* 【P338】「完成服务」提交中 loading（防止重复点击；原先无 loading，
       * 且失败仅 toast 一句「操作失败」，用户无法区分是自己没权限还是系统问题） */
      finishSubmitting: false,
      /* 【P335】关联财务合同完成态（t_pro_contracts.stage）
       *  1待执行 2已订金 3已全款 4完成 5取消 */
      detailContractStage: null,
      detailContractStageText: '',
      /* 【新功能】完成弹框 + 终态展示 */
      detailHeadcount: 0,
      detailFinishRemark: '',
      completeShow: false,
      completeHeadcount: '',
      completeRemark: '',
      /* 【P411】true=已终态后再进来修改（标题/按钮/提示走「修改」口径） */
      completeEditMode: false,
      completeSubmitting: false,
      /* 【P234】从消息进详情时记录来源 Tab（4=消息 / 5=快捷入口），
         关闭详情弹层后退回该页，避免「点条消息就跳到房态页、回来还得自己找路」。 */
      msgReturnTab: 4,
      quickItems: [
        { key: 'message',    label: '消息中心',   icon: svg('<path d="M4 19V5l8 4 8-4v14"/><path d="M4 19h16"/>') },
        { key: 'room_status',label: '房态',       icon: svg('<path d="M4 20V10l8-6 8 6v10H4z"/><path d="M10 20v-5h4v5"/>') },
        { key: 'room_order', label: '会议室订单', icon: svg('<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 11h18"/>') },
        { key: 'value_order',label: '增值订单',   icon: svg('<rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M4 10 6 4h12l2 6"/><path d="M12 4v6"/>') },
        { key: 'purchase',   label: '采购单',     icon: svg('<circle cx="9.5" cy="19.5" r="1.4"/><circle cx="17" cy="19.5" r="1.4"/><path d="M3 4h2.5l2.4 10.5h10.4L21 7H6"/>') },
        { key: 'stat',       label: '统计',       icon: svg('<path d="M5 19V9"/><path d="M12 19V5"/><path d="M19 19v-7"/>') }
      ]
    }
  },
  computed: {
    /* 【P249】三 Tab 定义（顺序：未读 / 待处理 / 全部），角标取自 msgCounts */
    /* 【P301 全站消息统一】Tab 固定 4 个、顺序固定：未读 → 待处理 → 待阅驳回 → 全部 */
    msgTabsView() {
      return [
        { value: 'unread',  label: '未读',     count: Number(this.msgCounts.unread)  || 0 },
        { value: 'pending', label: '待处理',   count: Number(this.msgCounts.pending) || 0 },
        { value: 'reject',  label: '待阅驳回', count: Number(this.msgCounts.reject)  || 0 },
        { value: 'all',     label: '全部',     count: 0 },
      ]
    },
    /* 【P301】四大岗位能力位：后端下发（运营端本身恒为 true，仍以后端为准） */
    msgIsCoreRole() {
      return this.msgIsCoreRoleRaw !== null ? !!this.msgIsCoreRoleRaw : true;
    },
    avatarText() {
      const n = this.profile.name || '运营'
      return String(n).slice(0, 1)
    },
    deptText() {
      return (this.profile.dept_names || []).join(' / ')
    },
    roleText() {
      return (this.profile.role_names || []).join(' / ')
    },
    purSumText() {
      return this.purSum > 0 ? ('合计 ' + money(this.purSum)) : ''
    },
    /** 【P169】房态详情：服务状态文案 */
    detailSvcText() {
      const t = { 0: '待推送', 1: '待店长确认', 2: '店长已确认', 3: '服务已完成', 4: '完成' }
      return t[this.detailSvc] || '待推送'
    },

    /** 【P335】订单阶段文案：优先用后端 stage_text，回退本地字典 */
    detailStageTextFull() {
      const t = { 1: '待执行', 2: '店长已确认', 3: '服务已完成', 4: '已完成(终态)' }
      return this.detailStageText || t[this.detailStage] || '待执行'
    },
    /* 【P236-fix】批量「全选」复选框状态
       ★★ 必须放在 computed，不能放 methods：
         放 methods 时 Vue 会当普通方法注册，而模板里 :checked="msgPickedAll" 却按 getter 求值
         ⇒ 组件初始化早期（data 未就绪）访问 msgRows.length 直接抛
         「Cannot read properties of undefined (reading 'length')」→ 运营端白屏进不去。
       ★ 仍加数组兜底，避免异常路径下再次炸掉整页。 */
    msgPickedAll() {
      const rows = this.msgRows || []
      const picked = this.msgPicked || []
      return rows.length > 0 && picked.length === rows.length
    }
  },
  watch: {
    tabIndex(v) {
      const sub = TAB_SUB[v]
      if (sub !== undefined) {
        if (sub === '') { this.subKey = '' } else { this.openSub(sub) }
      }
      /* 【P232-fix】★ 消息中心是内联 Tab（index 4），TAB_SUB[4]==='' ⇒ 上面这行不会走 openSub。
         切到消息页时**必须在这里直接拉数据** ——
         之前只在 openSub('message') 里拉，而「点九宫格」这条路径在某些时序下
         tabIndex 已经是 4（v-if 不重渲染），loadMessages 不会被调用 ⇒ 列表空白。 */
      if (v === 4) { this.loadMessages(true) }
      /* 【P176】切到房态 Tab（0）时确保数据已加载 + 重算滚动区高度
         仅在「尚未拉过门店列表」时补拉，避免每次切 Tab 都重复请求 */
      if (v === 0) {
        if (!this.storesData.length) { this.loadRoomStatus() }
        this.$nextTick(this.measureRoomScrollH)
      }
    }
  },
  created() {
    /* 【P420】★ 未登录 ⇒ 跳到运营端独立登录页 #/operation/login，**不拉任何业务接口**。
       原【P314】做法是「就地停在本页显示内联登录层」，结果是：
         · 运营端没有可分享的登录入口，必须先去 #/client 登录再靠角色分流进来；
         · 退出后落到 #/operation，业务代码先跑一轮 → 451 刷屏，再被内联层盖住。
       现在统一由独立登录页接管（退出登录也回那一页，见 clientAuth.js LOGIN_HOME）。
       showLogin 仍置 true 作为**兜底**：万一 replace 被拦截，内联层还能顶上，
       不至于让用户停在会狂发 451 的业务页上。 */
    if (!isLoggedIn()) {
      this.showLogin = true
      this.$router.replace('/operation/login')
      return
    }
    this.showLogin = false
    this.loadProfile()
    this.loadDashboard()
    this.loadUnread()
    /* 【P176】默认落在房态 Tab（tabIndex=0），watch 只响应「变化」⇒ 首屏需主动拉一次 */
    this.loadRoomStatus()
  },
  mounted() {
    /* 【P314】窗口宽度变化时更新 innerWidth（决定渲染 H5 登录还是 PC 登录） */
    this._onWinResizeLogin = () => { this.innerWidth = window.innerWidth }
    window.addEventListener('resize', this._onWinResizeLogin)
    /* 【P176】窗口尺寸变化时重算房态滚动区高度（房态现为内联 Tab，判据改为 tabIndex） */
    this._onWinResize = () => { if (!this.subKey && this.tabIndex === 0) this.measureRoomScrollH() }
    window.addEventListener('resize', this._onWinResize)
    /* 【P415】订阅全局 WS 的未读角标推送（运营端此前只 reconnect/close，从未订阅）
       没有订阅 ⇒ WS 连上了、服务端也推了，但页面收不到任何反应，气泡永远不动。 */
    try {
      crmWs.connect()
      this._offUnread = crmWs.on('unread', (d) => this.onUnread(d))
    } catch (e) { console.error('[operation] ws connect failed', e) }
  },
  beforeDestroy() {
    if (this._onWinResizeLogin) { window.removeEventListener('resize', this._onWinResizeLogin) }
    if (this._onWinResize) { window.removeEventListener('resize', this._onWinResize) }
    /* 【P415】退订 WS */
    if (this._offUnread) { try { this._offUnread() } catch (e) {} this._offUnread = null }
    /* 【P297】清掉消息请求的超时兜底定时器，避免组件已销毁还回调 */
    if (this._msgFinally) { clearTimeout(this._msgFinally); this._msgFinally = null }
  },
  methods: {
    money,

    /* 【P314】登录成功回调（内联登录层）：
       ① 先按「角色端」分流 —— 若登录的是销售/财务账号，不能停在运营端
       ② 运营账号：重连 WS（带新 token）+ 拉首屏四份数据 */
    onLoginSuccess(token, profile) {
      const scope = (profile && profile.app_scope) || ''
      if (scope && scope !== 'operation') {
        this.$router.replace({ path: '/' + scope })
        return
      }
      this.showLogin = false
      /* 【P30】新 token 必须强制重建连接才会被送去鉴权（旧 socket 带失效 token） */
      try { crmWs.reconnect(); crmWs.refresh() } catch (e) { /* ignore */ }
      this.$nextTick(() => {
        this.loadProfile()
        this.loadDashboard()
        this.loadUnread()
        this.loadRoomStatus()
      })
    },

    /* ---------------- 身份 ---------------- */
    loadProfile() {
      const cached = getProfile() || {}
      this.profile = Object.assign({}, cached)
      operationMyProfile({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.profile = Object.assign({}, cached, res.data)
          this.authDenied = false
        } else if (res && res.code === 403) {
          this.authDenied = true
          this.authMsg = res.msg || '无运营人员权限'
        }
      }).catch(err => {
        const msg = (err && err.msg) || ''
        if (String(msg).indexOf('运营') !== -1) {
          this.authDenied = true
          this.authMsg = msg
        }
      })
    },
    loadDashboard() {
      /* 【P112】返回 Promise，供首页下拉刷新收尾 */
      return operationDashboard({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.dash = res.data
          if (res.data.unread) { this.unread = res.data.unread }
        }
      }).catch(() => {})
    },
    loadUnread() {
      console.log('[MSG_FLOW] FE loadUnread.enter')
      operationMessageUnread({}).then(res => {
        if (res && res.code === 200 && res.data) { this.unread = res.data }
        console.log('[MSG_FLOW] FE loadUnread.done', { code: res && res.code, data: res && res.data })
      }).catch(() => {})
    },
    /* 【P393-④】从处理接口的响应里取回 unread 并立即写入气泡。
     *
     * 为什么需要它（气泡残留的真正原因）：
     *   · 处理接口（roomTodoConfirm / roomFinishService / roomComplete）后端已回传 unread；
     *   · 但前端原先只在成功后调 loadUnread() 再发一次请求；
     *   · 而 loadUnread 的 .catch(() => {}) 是静默的 —— 一旦这个请求失败/超时/被 ws 重连抢占，
     *     气泡数字就永远停在旧值；websocket 在断连期间也不推送 ⇒ 用户看到「处理完还有气泡」。
     *
     * 用法：在处理成功的分支里调用，后续 loadUnread() 回调会用权威值覆盖（双保险）。
     * 注意 unread 形如 {primary:0, cc:0, total:0}，用 total 驱动气泡。
     */
    applyUnreadFromResponse(data) {
      if (!data || typeof data !== 'object') return;
      const u = data.unread || data;
      if (u == null || typeof u !== 'object') return;
      const total = Number(u.total);
      if (isNaN(total)) return;
      this.unread = {
        primary: Number(u.primary) || 0,
        cc: Number(u.cc) || 0,
        total: total
      };
      console.log('[MSG_FLOW] FE applyUnread', { total: total, from: 'response' });
    },
    /* ============================================================
       【P415】WS 推送 → 未读气泡实时更新
       ------------------------------------------------------------
       运营端原本只调 crmWs.reconnect() / close()，**从未订阅 'unread' 事件**
       ⇒ 连接正常、服务端也推，但页面毫无反应（气泡不更新的前端根因）。
       payload：{m1,m2,m3,total}（后端 MessageBadgeService::refresh 口径）。
       运营端气泡用 total 驱动；primary/cc 分项由后端 unreadCount 计算，
       故收到推送后补拉一次权威值（双保险，失败静默）。
       ============================================================ */
    onUnread(d) {
      if (!d || typeof d !== 'object') { return }
      const m1 = Number(d.m1) || 0
      const m2 = Number(d.m2) || 0
      const m3 = Number(d.m3) || 0
      const total = (typeof d.total !== 'undefined') ? (Number(d.total) || 0) : (m1 + m2 + m3)

      /* ============================================================
         【P417】★★ 修「首页 / 快捷入口气泡一闪一闪、读完了还冒出来」
         ------------------------------------------------------------
         根因（两套口径 + 5s 轮询）：
           · WS 客户端每 5s 主动拉一次角标（websocket.js interval=5000）；
           · 推送值 m1~m3 是 **owner_id ∪ receiver 并集**口径（MessageBadgeService）；
           · 而首页 KPI / 气泡读的是 `unread`（operationMessageUnread → unreadCount，
             纯 receiver 口径）。实测同一时刻徽标 total=9、receiver 未读=1/6
             ⇒ 每 5s 把 9 写进来、loadUnread() 又写回 1/6 ⇒ 数字来回跳（一闪一闪）；
           · 已读之后 receiver 侧已清零，但徽标仍含 owner 侧残值（>0）
             ⇒ 气泡「消失 → 又冒出来 → 再消失」（用户说的「读取后消失」循环）。
         修法：
           ① **数值只认 REST**（loadUnread / loadDashboard），徽标一律不写进 unread；
           ② 徽标仅当「签名变化」时才作为触发器去拉一次权威值 ——
              同一份 5s 轮询结果不再产生任何渲染与请求。
         ============================================================ */
      const sig = [m1, m2, m3, total].join('|')
      if (sig === this._badgeSig) { return }
      this._badgeSig = sig
      console.log('[MSG_FLOW] FE ws.unread (trigger only)', { sig: sig, m1: m1, m2: m2, m3: m3 })
      try { this.loadUnread() } catch (e) {}
      if (this.subKey === 'message') { try { this.loadMsgSummary() } catch (e) {} }
    },
    /* 【P112】首页（含快捷入口）下拉刷新
       刷新对象 = KPI 看板（loadDashboard，含 5 个 Tab 的计数）+ 未读角标（loadUnread）。
       与 refreshCurrent() 同源，但那个是「子页」用的（会顺手刷 BillList），首页不需要。 */
    onHomeRefresh() {
      const done = () => { this.homeRefreshing = false }
      try {
        const p = this.loadDashboard()
        this.loadUnread()
        if (p && typeof p.then === 'function') { p.then(done, done) }
        else { setTimeout(done, 800) }
      } catch (e) { done() }
    },
    onLogout() {
      /* 【P118】logout() 内部已按「当前端」跳到本端登录页（运营 → #/operation），
         这里不要再二次硬编码跳 #/login，否则会覆盖掉正确落点 */
      logout()
    },

    /* 【P196】退出登录（与销售端 client.vue P46 同一交互）：
       点「账号 → 退出登录」先弹全站统一的 ConfirmDialog 二次确认，再执行登出。 */
    askLogout() {
      this.logoutShow = true
    },
    doLogout() {
      this.logoutShow = false
      /* 关掉 WS 推送，再清本地鉴权并回本端登录页（与销售端 doLogout 同一实现） */
      try { crmWs.close() } catch (e) { /* ignore */ }
      this.onLogout()
    },

    /* ---------------- 子页导航 ---------------- */
    openSub(key) {
      /* 【P176】房态已改为内联 Tab（非子页）⇒ 从快捷入口点进来时切 Tab 而非开子页，
         否则会因 subKey 非空把底部 Tab 栏隐藏，又变回「出不来」的老问题 */
      if (key === 'room_status') {
        this.subKey = ''
        this.tabIndex = 0
        if (!this.storesData.length) { this.loadRoomStatus() }
        this.$nextTick(this.measureRoomScrollH)
        return
      }
      if (key === 'stat') { this.$toast('统计模块待接入'); return }
      /* 【P235 修正】★ 消息中心**回到 subKey 子页**。
         走内联 Tab（tabIndex===4）会与「快捷入口」撞车 ——
         TABS 只有 5 项（0~4，4 就是快捷入口），两页判据不可能都是 4。
         底部 Tab 栏已改为「消息页也显示」（去掉了 !subKey 条件）+ 顶部加了返回箭头，
         所以既不会「进得去出不来」，也不会点不动 Tab。 */
      if (key === 'message') {
        this.subKey = 'message'
        this.subSeq++
        this.loadMessages(true)
        this.loadMsgSummary()
        this.$nextTick(this.measureHead)
        return
      }
      /* 【P224】★ 全部 Tab 均为内联页 ⇒ 统一「按 key 切到目标 Tab」，绝不再设 subKey。
         过去这里对 message 走 `this.subKey = key` 开二级子页，导致：
           ① 底部 Tab 栏（v-if="!authDenied && !subKey"）在消息页整条消失；
           ② 用户只能靠顶部返回箭头退出 = P176 明令要消灭的「进得去出不来」。
         改法：反查 TAB_SUB 拿到索引 → 切 tabIndex。
         subSeq 仍自增，强制 BillList 等列表组件重挂载重拉数据（:key="'room-' + subSeq"）。 */
      const target = Object.keys(TAB_SUB).find(i => TAB_SUB[i] === key)

      if (target !== undefined) {
        this.subKey = ''
        this.tabIndex = Number(target)
        this.subSeq++
        if (key === 'message') { this.loadMessages(true) }
        if (key === 'purchase') { this.loadPurchase(true) }
        this.$nextTick(this.measureHead)
        return
      }
      /* 兜底：未在 TAB_SUB 登记的 key 才退化为子页（保留扩展位） */
      this.subKey = key
      this.subSeq++
      this.loadMessages(true)
      this.measureHead()
    },
    /** 【P93】量出当前子页固定头部真实高度（三处 header 共用 ref="subHead"，v-if 同时只渲染一个） */
    measureHead() {
      this.$nextTick(() => {
        const el = this.$refs.subHead
        const h = el ? el.offsetHeight : 0
        if (h && h !== this.headH) { this.headH = h }
      })
    },
    closeSub() {
      /* 【P235】关闭子页时**回到来源页**：
         从快捷入口(4)进消息 → 关掉回快捷入口；其它子页回房态(0)。
         避免「关掉消息后落在房态页」让用户找不到北。 */
      const back = (this.msgReturnTab === 4) ? 4 : 0
      this.subKey = ''
      this.tabIndex = back
      if (back === 4) { this.$nextTick(() => { this.loadMessages(true) }) }
    },
    /** 【P235】底部 Tab 点击：任何子页下点 Tab 都要先清 subKey，否则页面不换 */
    onTabClick(i) {
      if (this.subKey) {
        this.subKey = ''
        this.msgReturnTab = i
      }
      this.tabIndex = i
    },

    /* ---------------- 消息中心（P244 三 Tab：未读 / 待处理 / 全部） ---------------- */
    /** 切 Tab。unread=未读(阅读态) · pending=待处理(is_action=0) · all=全部 */
    onMsgTab(k) {
      this.msgTab = k
      this.msgPicked = []
      this.loadMessages(true)
    },
    /** 【P244】批量确认：把勾选中的消息对应订单一次性确认 */
    onBatchConfirm(payload) {
      const sns = (payload && payload.sns) ? payload.sns : payload
      if (!Array.isArray(sns) || !sns.length) { this.$toast('请先勾选要确认的消息'); return }
      this.doConfirm(sns, '已确认，已通知运营负责人')
    },
    /** 【P244】拉角标计数（只取「未读」「待处理」两个） */
    loadMsgSummary() {
      operationMessageSummary({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.msgCounts = {
            unread:  Number(res.data.unread)  || 0,
            pending: Number(res.data.pending) || 0,
          }
        }
      }).catch(() => { /* 角标失败不打扰用户 */ })
    },
    loadMessages(reset) {
      /* 【P304】★★ 修「向下滚动分页失效」
       * 原实现三处致命问题：
       *   ① `const body = { page: 1, pageSize: 50 }` —— **page 硬编码 1**，
       *      无论怎么滚都只取第 1 页 ⇒ 「分页失效」的直接原因；
       *   ② 全站**没有任何一处调用 loadMessages(false)** ⇒ 上拉加载事件根本没接线；
       *   ③ 没有 finished 判定 ⇒ 到底后仍会反复请求。
       * 现在：reset=true → msgPage 归 1 并清 finished；reset=false → 沿用当前 msgPage。
       * ★ reset=true 才是「下拉刷新 / 切 Tab」，reset=false 是「上拉加载更多」。 */
      if (reset) { this.msgRows = []; this.msgPage = 1; this.msgFinished = false }
      /* 上拉加载中不重复触发（van-list 已会拦一道，这里再兜一层防竞态）*/
      if (!reset && (this.msgListLoading || this.msgFinished)) { return }
      this.msgLoading = true
      /* 【P304】★★ 两条路径都要占 listLoading 闸门：
       *   · 上拉(reset=false)：挡 van-list 连发
       *   · 下拉/切Tab(reset=true)：更关键 —— 首屏请求在途时 van-list 会 check()，
       *     看到 loading=false & finished=false 就 emit('load')，
       *     onMsgLoadMore 的 finished/loading 闸门此时都拦不住（reset 路径刚把 finished 清了）
       *     ⇒ 会并发再发一次 page=1，列表出现重复卡片。
       * 一并占位即可根治（van-list 见到 loading=true 就不会 emit）。 */
      this.msgListLoading = true
      /* 【P297】★ 必须是 **true** 而不是 false：
         父级用 refreshing 表达「正在刷新」，MessagePanel 监听它回落来把
         Vant 的 v-model 同步回 false（头部弹回 + status 复位）。
         若这里就置 false，等于刷新刚开始就告诉组件「结束了」，
         下拉动画会被立刻打断（表现为头部一闪就缩回）。 */
      this.msgRefreshing = true
      this.msgErr = ''
      /* 【P297】★★ 下拉刷新 loading 卡死修复（组件侧已修 v-model，这里补数据侧的收尾）
       * 1) 无论成功/失败/空列表/提前 return，都必须走到最后统一复位 —— 用 _msgFinally 兜底
       * 2) 加超时：请求迟迟不回来时也不能让「加载中」一直挂着（Vant 的 status 停在 loading
       *    会让 touchable=false，页面再也拉不动）
       */
      if (this._msgFinally) { try { clearTimeout(this._msgFinally) } catch (e) {} }
      this._msgFinally = setTimeout(() => {
        this._msgFinally = null
        this.msgLoading = false
        this.msgRefreshing = false
      }, 15000)
      /* 【P304】page 不再硬编码 1 —— 用 msgPage（reset 时归 1，上拉时沿用）*/
      const body = { page: this.msgPage, pageSize: this.msgPageSize }
      /* 【P244】三 Tab → 后端筛选参数：
       *   pending 待处理 → confirm=0（role=1 主送 + is_action=0）
       *   unread  未读   → is_read=0（阅读态，跨业务态，不限 role）
       *   all     全部   → 不传筛选，后端返回该账号全部消息（含已确认/抄送）
       * ★ 「已确认」不再单独占 Tab，已并入「全部」（用户 10-07 确认）。 */
      /* 【P281】按 Tab 下推过滤条件（后端 P281 新增 msg_type 维度）
       *   未读   = type=0 且 is_read=0
       *   待处理 = type=1 且 is_action=0（role=1 主送，后端已限定）
       *   全部   = 不加 type 条件（不传）
       * ★ 务必传 msg_type，否则后端只能按 is_read/confirm 过滤，
       *   会把 type=1 的待审批消息混进「未读」Tab。 */
      if (this.msgTab === 'pending') { body.confirm = 0; body.msg_type = 1; }
      if (this.msgTab === 'unread')  { body.is_read = 0; body.msg_type = 0; }
      /* 【P301】待阅驳回 Tab：传 msg_tab=reject（后端按 type=2 且 is_read=0 过滤） */
      if (this.msgTab === 'reject')  { body.msg_tab = 'reject'; }
      operationMessageList(body).then(res => {
        /* 【P232-fix】把「接口实际返回了什么」显式落到页面上 ——
           原先只 console.log，线上列表空时完全无从判断是 code 不对、还是 data 结构不符。
           msgErr 现在承载「诊断信息」，成功时自动清空。 */
        /* 【P283】★ 去掉用户可见的调试提示。
         * 原实现（P232 加的「诊断信息」）把「接口返回 0 条（total=0）」写进 msgErr，
         * 而 msgErr 会渲染到页面（MessagePanel 的 .mp-err）⇒ **调试话术暴露给用户**。
         * 「共 0 条」是**正常业务状态**（本来就没消息），不是错误。
         * 现在：真实错误（无响应 / code≠200 / 异常）仍提示；空列表**不提示**，
         *       交给空态区显示 P281 提供的按 Tab 文案。 */
        /* 【P301】★ res 为空 = 登录失效（request.js 拦截器已改 reject，但保险起见给出准确文案）。
           旧文案「无响应（res 为空）」会让人以为是网络/后端问题，误导排查方向。 */
        if (!res) {
          this.msgErr = '登录已失效，请重新登录';
          this.msgLoading = false; this.msgRefreshing = false;
          return;
        }
        if (res.code !== 200) {
          this.msgErr = '接口返回 code=' + res.code + ' msg=' + (res.msg || '-')
            + (res.code === 452 ? '（token 失效，请重新登录）' : '')
            + (res.code === 451 ? '（缺少 token）' : '')
          /* 【P304】拉取失败 ⇒ 标记到底，避免 van-list 停在 loading 反复重试同一页；
             用户下拉刷新（reset=true）会重新打开闸门。 */
          this.msgFinished = true
          return
        }
        const d = res.data
        /* 【P301】接收后端下发的「四大岗位」能力位 */
        if (d && typeof d.is_core_role !== 'undefined') {
          this.msgIsCoreRoleRaw = !!d.is_core_role
        }
        const list = (d && Array.isArray(d.data)) ? d.data : []
        if (!list.length) {
          /* 空列表 = 正常（无消息），**不设错误提示**；诊断信息只进 console */
          this.msgErr = ''
          console.log('[MSG_FLOW] FE loadMessages.empty', {
            tab: this.msgTab, total: d && d.total != null ? d.total : null,
            api: 'operation/messageList', msg_type: body.msg_type != null ? body.msg_type : 'none'
          })
        } else {
          this.msgErr = ''
        }
        let rows = list
        if (this.msgTab === 'pending') {
          /* 待处理：主送 + 未确认（后端已过滤，这里双保险） */
          rows = list.filter(x => Number(x.role) === 1 && Number(x.is_action) === 0)
        } else if (this.msgTab === 'unread') {
          /* 未读：阅读态，跨业务态 */
          rows = list.filter(x => Number(x.is_read) === 0)
        }
        this.msgRows = reset ? rows : this.msgRows.concat(rows)
        this.msgTotal = d ? (d.total || this.msgRows.length) : this.msgRows.length
        /* 【P304】★ 分页到底判定（两个条件任一命中即 finished，防无限滚请求）
         *   ① 后端返回 total（可信时以 total 为准：累计条数 >= total ⇒ 到底）
         *   ② 无 total 时按「本次不足一页」判定
         * ⚠ 必须用 **后端原始 list.length**（list）判 ①，用 rows（前端过滤后）会偏小误判到底；
         *   但 finished 的兜底②又该看 rows —— 若前端过滤后本页不足一页也大概率到底。
         *   这里采取保守策略：list 不足一页 或 rows 不足一页 ⇒ 到底。 */
        const rawShort = list.length < this.msgPageSize
        const filteredShort = rows.length < this.msgPageSize
        const hitTotal = (d && d.total != null && this.msgRows.length >= d.total)
        this.msgFinished = reset
          ? (rawShort || filteredShort || !!hitTotal)
          : (this.msgFinished || rawShort || filteredShort || !!hitTotal)
        /* 只有真正还有下一页时才把页码推进，finished 后不再 +1（防止无意义自增）*/
        if (!this.msgFinished) { this.msgPage = this.msgPage + 1 }
        console.log('[MSG_FLOW] FE loadMessages.page', {
          tab: this.msgTab, reset: !!reset, reqPage: body.page,
          rawLen: list.length, rowLen: rows.length, acc: this.msgRows.length,
          total: d && d.total != null ? d.total : null,
          finished: this.msgFinished, nextPage: this.msgPage
        })
      }).catch(e => {
        this.msgErr = '请求异常：' + ((e && e.message) || '未知错误')
      }).then(() => {
        /* 【P297】统一收尾：成功 / code≠200 / 提前 return / 网络异常 都会走到这里。
           ★ 清掉超时兜底定时器，避免它稍后再跑一次把状态又改一遍。 */
        if (this._msgFinally) { clearTimeout(this._msgFinally); this._msgFinally = null }
        this.msgLoading = false
        this.msgRefreshing = false
        /* 【P304】必须复位，否则 van-list 卡在 loading 再也不 emit load */
        this.msgListLoading = false
      })
    },
    /* 【P304】上拉触底 → 加载下一页。
       ★ 组件只在 finished=false / listLoading=false 时才 emit('load-more')，
         这里再判一次 msgPage，防止并发重复触发。 */
    onMsgLoadMore() {
      /* 只判 finished：msgListLoading 的并发闸门在 loadMessages 里已有，
         这里再判一次会与「请求在途但已完成」的时序撞车导致静默丢请求。 */
      if (this.msgFinished) { return }
      console.log('[MSG_FLOW] FE onMsgLoadMore', { tab: this.msgTab, page: this.msgPage })
      this.loadMessages(false)
    },
    openMessage(m) {
      console.log('[MSG_FLOW] FE openMessage.enter', { sn: m.sn, is_read: m.is_read })
      if (!m.is_read) {
        operationMessageRead({ sn: m.sn }).then(res => {
          console.log('[MSG_FLOW] FE openMessage.read', { sn: m.sn, code: res && res.code, unread: res && res.data })
          if (res && res.code === 200) {
            m.is_read = 1
            if (res.data) { this.unread = res.data }
          }
        }).catch(() => {})
      }
      /* 【P232/P234-fix】★ 会议室订单类消息 → **同页打开该订单的详情弹层**。
         · 原先只 openSub('room_order') 跳订单列表，用户还得自己再找那一单 ⇒ 实际等于「点了没反应」；
         · 但如果为了开弹层把 tabIndex 切到 0（房态，弹层的宿主容器），
           用户关闭弹层后会**落在房态页**，等于「莫名其妙跳走」。
         折中方案：**记录来源页**，弹层关闭后回消息页；
         且优先尝试「不改 Tab 直接开弹层」（当 message 页 DOM 里恰好已有弹层宿主时可用）。 */
      /* 【P359 BUG 修复】page_api 匹配串写错，导致点了没反应。
         实际值（FinanceDict::PAGE_MEETING，PC/H5 共用）：
             page_api = 'operation_meetingRoomOrders'
             page_url = '/operation_meetingRoomOrders/info/detail'
         原判断 `.indexOf('operation_room') !== -1` —— **数据库里根本不存在这个子串**
         （是 operation_**meetingRoom**Orders），所以永远匹配不上 ⇒ 弹层不开。
         这里改为同时兼容历史/别名写法。 */
      if ((m.page_api || '').indexOf('operation_meetingRoomOrders') !== -1
          || (m.page_api || '').indexOf('operation_room') !== -1) {
        const orderSn = this.orderSnOf(m)
        if (orderSn) {
          /* 记住来源页，关闭详情后要退回来（【P235】） */
          this.msgReturnTab = 4
          this.openRoomDetail(orderSn)
        } else {
          this.closeSub()
          this.$nextTick(() => { this.openSub('room_order') })
        }
      }
    },

    /* ================= 【P281】批量操作：按 Tab 分派到三个新接口 ================= */
    /* 说明：MessagePanel 已内置 batchAction=read|delete|confirm 三种批量条
     *   （含各自文案与二次确认），本方法只负责按动作调对应接口。
     *   · unread  → messageReadBatch    （批量已读，参考销售端按勾选逐条置）
     *   · pending → messageConfirmBatch （批量「我知道了」，后端已含 owner 越权校验）
     *   * all     → messageDeleteBatch  （★物理删除，按用户 10-10 要求不用软删） */
    onMsgBatch(payload) {
      const sns = payload && Array.isArray(payload.sns) ? payload.sns
                : (Array.isArray(payload) ? payload : []);
      if (!sns.length) { this.$toast('请先勾选'); return; }
      const action = this.batchActionForTab();
      /* 【P400】★★ 批量确认必须与房态详情页「我知道了」**同一套逻辑**（订单维度 confirmTodo）。
       *
       * 原实现走 operationMessageConfirmBatch（消息维度），它只做两件事：
       *   ① pro_orders_meeting_room.service_status = 2
       *   ② saas_data_message.is_action = 1
       * 相比「我知道了」（OperationPushService::confirmTodo）**缺了 4 个关键副作用**：
       *   · stage 不推进（停在 1 待执行 ⇒ 房态详情仍显示可点「我知道了」）
       *   · 不写 service_finish_time / service_finish_user（无确认人留痕）
       *   · 不调 markApprovalDone（审批单永远停不到「已处理」）
       *   · 不走 upwardHeads（上级一条通知都收不到）
       * ⇒ 用户观感就是「点了确认，但房态没变、审批单没变、上级没收到」＝功能异常。
       * 现改为走 operationMessageTodoConfirmBatch：后端把消息 sn 反解成订单 sn 后
       * 直接调 OperationPushService::confirmTodo，与房态详情页「我知道了」同一函数。
       */
      if (action === 'confirm') {
        this.batchConfirmAsTodo(sns);
        return;
      }
      /* 【P339 BUG 修复】原来写成 `operationApi.messageReadBatch` —— `operationApi`
       * 这个命名空间**从未存在**（api.js 是平铺导出 operationMessageReadBatch 等），
       * ⇒ 用户「未读」全选后点「标记已读」直接 ReferenceError: operationApi is not defined。
       * 现改为引用顶部已 import 的平铺函数名。 */
      const api =
        action === 'delete'   ? operationMessageDeleteBatch :
                                operationMessageReadBatch;
      if (typeof api !== 'function') { this.$toast('当前 Tab 不支持批量操作'); return; }

      /* ---------- 【P381】★去掉本层的二次确认弹窗，收到事件直接执行 ----------
       *
       * 用户 10-15 15:00 反馈：「tab『未读』『全部』，在选择批量以后，弹出的对话框重复了，
       * 需要确认了两次太啰嗦了，改成弹出一次确认后操作。」
       *
       * 根因：两层确认同时存在
       *   ① MessagePanel.vue 内置 ConfirmDialog（onAskBatch → dialogConfirm → doBatch → $emit('batch')）
       *   ② 本方法收到 batch 事件后又把 msgBatchShow=true 弹第二次
       *      （原注释「组件已弹一次，这里给业务侧再兜一层语义」就是元凶）
       *
       * 改法：确认框统一由组件层负责（那边文案本就已按 Tab 区分：
       *      批量已读 / 批量我知道 / 批量删除），本层只负责调接口。
       */
      this.msgConfirming = true;
      api({ sns: sns, msg_type: this.msgTab === 'unread' ? 0 : (this.msgTab === 'pending' ? 1 : -1) })
        .then(res => {
          this.msgConfirming = false;
          if (!res || res.code !== 200) {
            this.$toast((res && res.msg) ? res.msg : '操作失败');
            return;
          }
          const n = (res.data && (res.data.affected || res.data.orders || res.data.receivers)) || '';
          this.$toast((res.msg || '操作成功') + (n !== '' ? '（' + n + '）' : ''));
          this.loadMessages(true);
        })
        .catch(() => { this.msgConfirming = false; });
    },

    /* 【P400】批量确认执行器：直接把**消息 sn** 交给后端 messageTodoConfirmBatch。
     *
     * 为什么不再在前端把消息 sn 解析成订单 sn：
     *   ① 后端 resolveMessageRoomSn 才是权威口径（page_sn → 消息表 parent_sn →
     *      支付单 order_meeting_room 反查），前端复刻必然漂移；
     *   ② 后端还要按结果做「消息层补刀」（confirmTodo 只按 parent_sn=订单sn 收口，
     *      历史消息会漏），前端拿不到这个信息；
     *   ③ 少一次 hex 正则猜测，少一类「解析出脏 sn 直接请求失败」的事故。
     * 语义仍等价房态详情页「我知道了」—— 后端就是调同一个 confirmTodo。
     */
    batchConfirmAsTodo(msgSns) {
      if (this.msgConfirming) { return; }
      this.msgConfirming = true;
      console.log('[MSG_FLOW] FE todoConfirmBatch.enter', { sns: msgSns });
      operationMessageTodoConfirmBatch({ sns: msgSns }).then(res => {
        this.msgConfirming = false;
        console.log('[MSG_FLOW] FE todoConfirmBatch.done', { code: res && res.code, data: res && res.data });
        this.applyConfirmResult(res, '已确认');
      }).catch(() => { this.msgConfirming = false; });
    },

    /* 【P281】ConfirmDialog 确认后真正执行 */
    onMsgBatchConfirm() {
      this.msgBatchShow = false;
      const sns = this._msgBatchSns || [];
      this.msgBatchTip = '';
      if (typeof this._msgBatchRun === 'function') { this._msgBatchRun(sns); }
      this._msgBatchRun = null;
      this._msgBatchSns = [];
    },
    /* ConfirmDialog 取消 */
    onMsgBatchCancel() {
      this.msgBatchShow = false;
      this.msgBatchTip = '';
      this._msgBatchRun = null;
      this._msgBatchSns = [];
    },

    /* ================= 【P281】消息 Tab 的批量动作与空态文案 ================= */
    /* 各 Tab 的批量动作（★ P400 定版，勿再混用）：
     *   unread  → 批量已读     （参考销售端：按勾选逐条置）
     *   pending → 批量「我知道了」（订单维度，走 messageTodoConfirmBatch ≡ 房态「我知道了」）
     *   all     → 批量删除     （★ 用户 10-20 明确：**全部 Tab 不开放批量确认，只保留批量删除**）
     *
     * ⚠️ 「全部」Tab 曾经尝试过「勾选全是待办时自动切成批量确认」（P400 初版
     *    allPickedConfirmable），用户当天明确否决并收窄需求 ⇒ 已整体回退。
     *    若将来要恢复，必须先拿到用户确认，且不要在 pending 分支之外
     *    再返回 'confirm'，否则三个 Tab 的动作语义会再次交叉。
     */
    /* 【P301 全站统一】待处理 / 待阅驳回 默认禁止批量（仅单点进详情）
     * 【P308】★ 运营端例外：恢复「待处理中 → 批量『我知道了』」历史功能
     *   （与模板上的 :allow-pending-batch="true" 保持一致）。 */
    batchEnabledForTab() {
      if (this.msgTab === 'unread')  { return this.msgIsCoreRole; }
      if (this.msgTab === 'pending') { return true; }   // ★ P308 恢复
      if (this.msgTab === 'all')     { return true; }
      return false;   // reject / 其它
    },
    /* 动作：未读=批量已读 / 待处理=批量确认 / 全部=批量删除（reject 无批量） */
    batchActionForTab() {
      if (this.msgTab === 'unread')  { return 'read'; }
      /* 【P308】★ 恢复：待处理 → confirm（批量「我知道了」）。
         P301 全站统一时这里被清成 ''（配合「待处理永禁批量」），
         现在批量重新开放，动作类型必须一并恢复，否则组件拿不到按钮文案。 */
      if (this.msgTab === 'pending') { return 'confirm'; }
      /* 【P400 定版】★ 全部 Tab **永远**批量删除，不开放批量确认。
       *   用户 10-20 明确：「『全部』Tab 不开放批量确认，只保留批量删除」。
       *   注意这里的 'delete' 是物理删除（messageDeleteBatch），
       *   绝不能返回 'confirm' —— 否则「全部」会变成批量确认，与用户口径冲突。 */
      if (this.msgTab === 'all')     { return 'delete'; }
      return '';
    },
    /* 各 Tab 的批量按钮文案 */
    batchTextForTab() {
      if (this.msgTab === 'unread')  { return '批量已读'; }
      /* 【P308】★ 恢复：待处理 → 「批量我知道」 */
      if (this.msgTab === 'pending') { return '批量我知道'; }
      if (this.msgTab === 'all')     { return '批量删除'; }
      return '';
    },
    /* 【P281】空态文案：**去掉「0 条」这类测试提示**，
     * 按 Tab 给一句可读说明（原来所有 Tab 共用一句，且会把 total=0 直接抛给用户）。 */
    msgEmptyTextForTab() {
      if (this.msgTab === 'unread')  { return '没有未读消息'; }
      if (this.msgTab === 'pending') { return '没有待处理的服务'; }
      if (this.msgTab === 'reject')  { return '没有待阅驳回的消息'; }
      return '还没有任何消息记录';
    },

    /* ================= 【P232】批量/单条「确认（我知道了）」 ================= */
    isPicked(m) {
      return (this.msgPicked || []).indexOf(m.sn) >= 0;
    },
    /* 【P236-fix】msgPickedAll 已迁到 computed（见上方 computed 区块）——
       放在 methods 里会被 Vue 当普通方法，而模板按 getter 求值，导致初始化期崩溃。 */
    togglePick(m) {
      if (!this.msgPicked) { this.msgPicked = [] }
      const i = this.msgPicked.indexOf(m.sn);
      if (i >= 0) { this.msgPicked.splice(i, 1) } else { this.msgPicked.push(m.sn) }
    },
    togglePickAll(e) {
      if (e && e.target && e.target.checked) {
        this.msgPicked = (this.msgRows || []).map(m => m.sn);
      } else {
        this.msgPicked = [];
      }
    },
    /* ---------- 【P384】★把消息对应到会议室订单 sn ----------
     *
     * 用户 10-15 16:07 反馈：「消息点击进入会议室订单的时候报错了，是传 sn 的问题，
     *   SN 的数据用 t_saas_data_message 里面 parent_sn，是 t_pro_orders_meeting_room 的 sn。」
     *
     * 根因（cs10 实测）：
     *   消息 #11351 的 page_api=operation_meetingRoomOrders（声明跳订单详情），
     *   但 parent_sn=bd1d7c9ac85c… 实际命中表是 **t_pro_orders_payment（支付单）**
     *   与 t_app_finance_trace，**不是会议订单 sn**。
     *   ⇒ orderSnOf() 原样返回它 → openRoomDetail(支付单sn) → getClientOrderDetailData 查不到 → 报错。
     *
     *   为什么会写错：#11351 是 13:53 由 P380 之前的「H5 自造文案」路径生成的
     *   （content='支付单「xxx」审批通过，会议室订单待你确认与服务（订单：sn1、sn2）'），
     *   那条路径把**支付单 sn** 填进了 parent_sn。
     *   P380 新写的 writePcAlignedRoomMessages() 已改为写**会议订单 sn**
     *   （#11362/#11364 验证 parent_sn 正确命中），故新消息不会再产生此问题。
     *
     * 修复（**后端读层兜底 + 前端三层降级**）：
     *   【P384-B】后端 listMessages 新增 room_sn 字段，读取时就把脏 parent_sn 反解成真订单 sn：
     *     ① page_sn 命中订单表 → 用它
     *② parent_sn 命中订单表 → 用它（P380 之后的新消息都走这条）
     *     ③ parent_sn 命中的是支付单 → 用支付单 order_meeting_room 字段反查订单 sn（#11351）
     *     ④ 都不行 → 留空串，由前端回退到订单列表，绝不带着脏 sn 去请求
     *   前端按同样优先级降级，room_sn 缺失时仍能在纯前端侧自愈。
     */
    orderSnOf(m) {
      if (!m) { return ''; }

      /* ① 后端已解析好的 room_sn（最权威，listMessages 直接下发） */
      if (m.room_sn) { return String(m.room_sn); }

      /* ② page_sn（语义上就是"落地页 sn"，后端未解析时前端自用） */
      if (m.page_sn) { return String(m.page_sn); }

      const psn = m.parent_sn ? String(m.parent_sn) : '';
      const txt = String(m.content || '');
      /* 正文里所有 32 位 hex（支付单 sn 与会议订单 sn 都是这个形态） */
      const allHex = (txt.match(/[0-9a-f]{32}/gi) || []).map(x => String(x));

      /* ③ parent_sn 存在时，取**第一个不等于 parent_sn** 的 sn。
       * ⚠️ 这里绝不能取「正文第一个 hex」—— #11351 正文开头就是支付单 sn：
       *     '支付单「bd1d7c9a…」审批通过，会议室订单待你确认与服务（订单：4bc0d6e8…、c42db591…）'
       * 取第一个仍然会拿到支付单 sn，仍会报错。
       * 若正文里没有别的 sn ⇒ parent_sn 就是唯一候选，直接用。 */
      if (psn) {
        const other = allHex.filter(x => x.toLowerCase() !== psn.toLowerCase());
        if (other.length) { return other[0]; }
        return psn;
      }

      /* ④ parent_sn 为空：取正文第一个 sn */
      if (allHex.length) { return allHex[0]; }

      return '';
    },
    confirmPicked() {
      /* 【P400】取消重复实现：原来这里是 batchConfirmAsTodo 的旧副本（均按 orderSnOf
       * 解析消息 sn → 订单 sn），两套逻辑并存必然分叉。现统一委托给 batchConfirmAsTodo，
       * 保证「父级勾选确认」与「面板批量确认」走完全相同的解析与提示口径。
       * 保留方法名是因为历史模板/外部调用可能引用，删掉风险更大。 */
      this.batchConfirmAsTodo(this.msgPicked || []);
    },
    /** 订单详情内的单条确认 */
    confirmOrder(sn) {
      if (!sn) { this.$toast('缺少订单 sn'); return }
      this.doConfirm([sn], '已确认');
    },
    /* 【P400】从 confirmTodo 返回的 details 里取一条最贴切的原因，供批量提示用。
       details 结构：[{sn, status:'ok'|'skip'|'denied'|'error'|'warn', msg, ...}]
       优先级：error/denied（真失败）> 其它非 ok/warn > ''。
       ★ 跳过 'warn' —— warn 是「订单已确认成功，但通知运营负责人失败」，
         与 ok 同时出现，拿它当失败原因会误导用户。 */
    confirmFailReason(d) {
      const list = (d && Array.isArray(d.details)) ? d.details : [];
      const pick = (allowWarn) => {
        for (let i = 0; i < list.length; i++) {
          const it = list[i] || {};
          const st = String(it.status || '');
          if (st === 'ok') { continue; }
          if (!allowWarn && st === 'warn') { continue; }
          if (it.msg) { return String(it.msg); }
        }
        return '';
      };
      let why = '';
      for (let i = 0; i < list.length; i++) {
        const st = String((list[i] || {}).status || '');
        if ((st === 'error' || st === 'denied') && list[i].msg) { why = String(list[i].msg); break; }
      }
      if (why) { return why; }
      why = pick(false);
      return why || pick(true);
    },
    /* 【P400】确认结果的统一收口：房态详情页 doConfirm 与消息中心 batchConfirmAsTodo 共用。
     *   原来这段逻辑只写在 doConfirm 里，批量走另一个接口时提示口径会分叉。 */
    applyConfirmResult(res, okTip) {
      if (res && res.code === 200) {
        const d0 = (res && res.data) || {};
        const nOk = Number(d0.ok || 0);
        const nFail = Number(d0.fail || 0);
        const nSkip = Number(d0.skip || 0);
        const nIgn = Number(d0.ignored || 0);
        let tip = res.msg || okTip;
        if (nFail > 0 || nSkip > 0) {
          const why = this.confirmFailReason(d0);
          tip = '已确认 ' + nOk + ' 条';
          if (nFail > 0) { tip += '，失败 ' + nFail + ' 条' + (why ? '（' + why + '）' : ''); }
          if (nSkip > 0) { tip += '，跳过 ' + nSkip + ' 条'; }
        }
        if (nIgn > 0) { tip += '（' + nIgn + ' 条无关联订单已忽略）'; }
        this.$toast(tip, (nFail > 0) ? 'fail' : undefined);
        this.msgPicked = [];
        /* 【P393-④】先乐观消泡：用后端回传的 unread 立即更新气泡 */
        this.applyUnreadFromResponse(d0);
        this.loadUnread();
        this.loadMessages(true);
        /* 【P240】确认后「待确认」减、「已确认」增 ⇒ 必须刷新角标 */
        this.loadMsgSummary();
        /* 会议室订单列表也要刷新：service_status 已推进（确认态） */
        this.subSeq++;
      } else {
        /* 【P400】失败时带出具体原因（如「无权限：仅该订单负责人可确认」），
           而不是只吐一句「确认失败」让用户无从下手。 */
        const why = this.confirmFailReason((res && res.data) || {});
        const base = (res && res.msg) || '确认失败';
        this.$toast(why ? (base + '：' + why) : base, 'fail');
      }
    },
    doConfirm(sns, okTip) {
      if (this.msgConfirming) return;
      this.msgConfirming = true;
      console.log('[MSG_FLOW] FE confirm.enter', { sns: sns });
      operationRoomTodoConfirm({ sns: sns }).then(res => {
        this.msgConfirming = false;
        console.log('[MSG_FLOW] FE confirm.done', { code: res && res.code, data: res && res.data });
        this.applyConfirmResult(res, okTip);
      }).catch(() => { this.msgConfirming = false; });
    },
    readAll() {
      console.log('[MSG_FLOW] FE readAll.enter')
      operationMessageReadAll({}).then(res => {
        console.log('[MSG_FLOW] FE readAll.done', { code: res && res.code, unread: res && res.data && res.data.unread })
        if (res && res.code === 200) {
          this.$toast('已全部标为已读')
          this.msgRows = this.msgRows.map(x => Object.assign({}, x, { is_read: 1 }))
          if (res.data && res.data.unread) { this.unread = res.data.unread }
        }
      }).catch(() => {})
    },

    /* 【P281】统一执行器：承接 ConfirmDialog 的确认动作 */
    opDlgConfirm() {
      this.opDlgShow = false;
      const fn = this.opDlgFn;
      this.opDlgFn = null;
      if (typeof fn === 'function') { fn(); }
    },
    opDlgCancel() {
      this.opDlgShow = false;
      this.opDlgFn = null;
    },

    /* ---------------- 运营动作：推送待办 / 完成服务 ---------------- */
    onPushTodo(item) {
      if (!item || !item.sn) { return }
      /* 【P281】$dialog → 自研 ConfirmDialog（项目无 $dialog，会抛错） */
      /* 【P340 BUG 修复】同 onFinish：必须置 true 才会弹确认框 */
      this.opDlgTitle = '推送待办';
      /* 【P340】title 改静态后，动作名并入 desc 首行 */
      this.opDlgDesc = '即将【推送待办】给门店店长，并逐级抄送上级负责人。是否继续？';
      this.opDlgFn = () => {
        operationRoomTodoPush({ sn: item.sn }).then(res => {
          const d = res && res.data
          console.log('[MSG_FLOW] FE onPushTodo.done', { sn: item.sn, code: res && res.code, ok: d && d.ok, skip: d && d.skip, details: d && d.details })
          if (res && res.code === 200 && d) {
            suppressGlobalError()
            const ok = d.ok || 0
            const skip = d.skip || 0
            const det = (d.details || [])[0] || {}
            if (ok > 0) {
              this.$toast('推送成功')
            } else if (skip > 0) {
              this.$toast(det.msg || '已推送过，跳过')
            } else {
              this.$toast(det.msg || '推送失败')
            }
            this.refreshCurrent()
          } else {
            this.$toast((res && res.msg) || '推送失败')
          }
        }).catch(() => {})
      }
      /* 【P340】弹确认框（直接同步赋值；$nextTick 会与 vant lazy-render 抢时序导致不渲染） */
      this.opDlgShow = true;
    },
    onFinish(item) {
      if (!item || !item.sn) { return }
      /* 【P338】防重复提交：进行中直接忽略 */
      if (this.finishSubmitting) { return }
      /* 【P281】$dialog → 自研 ConfirmDialog */
      /* 【P340 BUG 修复】原来只写 `this.opDlgShow = false`（沿用旧 $dialog 时代
         「先关再开」的残留写法），**全文件没有一处 `opDlgShow = true`** ⇒
         点「完成服务」/「推送待办」只是把 title/desc/fn 赋值了，确认弹层永远不弹，
         用户观感＝「点了没反应」。这里必须置 true 才会弹 ConfirmDialog。 */
      this.opDlgTitle = '完成服务';
      /* 【P340】title 改静态后，动作名并入 desc 首行，信息不丢 */
      this.opDlgDesc = '即将【完成服务】该会议室订单。系统将：①把该订单置为「服务已完成」；②若该订单所属财务合同下全部会议室订单均已完成，自动把财务合同置为「完成」；③通知管理者。';
      this.opDlgFn = () => {
        this.finishSubmitting = true;
        operationRoomFinishService({ sn: item.sn, remark: '' }).then(res => {
          const d = res && res.data
          console.log('[MSG_FLOW] FE onFinish.done', { sn: item.sn, code: res && res.code, ok: d && d.ok })
          if (res && res.code === 200 && d && d.ok) {
            suppressGlobalError()
            /* 【P338】把后端回传的合同聚合结果一并告知，用户能确认「合同是否也被置为完成」 */
            const conMsg = d.contract_done
              ? '，关联财务合同已同步置为完成'
              : (d.contract_pending ? '' : (d.contract_missed ? '（该订单未关联财务合同）' : ''))
            this.$toast((d.msg || '服务已完成') + conMsg)
            this.refreshCurrent()
            /* 【P282】★补刷详情服务状态：原来只 refreshCurrent()（刷列表），
             * 详情页 detailSvc 仍是旧值 ⇒ 按钮不变灰、可重复点。
             * 与 confirmFromDetail 的 syncDetailSvc 对齐。 */
            this.syncDetailSvc(item.sn)
            /* 【P393-④】处理成功后**立即**用后端回传的 unread 消掉气泡。
             * 原来只靠 refreshCurrent() → loadUnread() 二次请求；
             * 该请求失败时被 .catch(()=>{}) 静默吞掉 ⇒ 气泡一直挂着，
             * 而 websocket 在断连/重连期间也不会推送 ⇒ 用户观感「处理完还有气泡」。
             * 后端已在 finishService 里回传 unread（见 OperationPushService::unreadCount），
             * 这里做乐观更新；随后 loadUnread() 的回调会用权威值覆盖。 */
            this.applyUnreadFromResponse(d)
          } else {
            /* 【P338】失败原因原样透出（无权限 / 订单不存在 / 并发等），
             * 不再统一吞成「操作失败」——这是「点了没反应」体感的主要来源。 */
            this.$toast((res && res.msg) || '操作失败，请稍后重试')
          }
        }).catch(() => { this.$toast('网络异常，请稍后重试') })
          .finally(() => { this.finishSubmitting = false })
      }
      /* 【P340】弹确认框（直接同步赋值；$nextTick 会与 vant lazy-render 抢时序导致不渲染） */
      this.opDlgShow = true;
    },
    refreshCurrent() {
      const bl = this.$refs.billList
      if (bl && typeof bl.onRefresh === 'function') { bl.onRefresh() }
      this.loadDashboard()
      this.loadUnread()
    },

    /* ---------------- 采购单 ---------------- */
    loadPurchase(reset) {
      if (reset) { this.purRows = [] }
      this.purLoading = true
      operationPurchaseList({ page: 1, pageSize: 30, searchText: this.purKw }).then(res => {
        const d = (res && res.code === 200) ? res.data : null
        const list = d && Array.isArray(d.data) ? d.data : []
        this.purRows = reset ? list : this.purRows.concat(list)
        this.purTotal = d ? d.total : this.purRows.length
      }).catch(() => {}).then(() => {
        this.purLoading = false
        this.purRefreshing = false
      })
      operationPurchaseSum({ searchText: this.purKw }).then(res => {
        if (res && res.code === 200 && res.data) { this.purSum = Number(res.data.amount || 0) }
      }).catch(() => {})
    },

    /* ---------------- 房态（【P169】与销售端 client.vue 1:1 同构） ---------------- */

    /** 入口：拉取本周日期区间 → 门店列表 → 首个门店房态网格 */
    loadRoomStatus() {
      this.roomLoading = true
      operationGetRoomDate({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.roomCurDate = res.data
          this.roomDateRangeValue = res.data['weekRangeValue'] || []
          this.getStoresData()
        } else {
          this.roomLoading = false
        }
      }).catch(() => { this.roomLoading = false })
    },
    getStoresData() {
      operationGetStores({}).then(res => {
        if (res && res.code === 200) {
          this.storesData = res.data || []
          if (this.storesData.length > 0) {
            this.storeSN = this.storesData[0].sn
            this.getRoomsData()
          } else {
            this.roomLoading = false
          }
        } else {
          this.roomLoading = false
        }
      }).catch(() => { this.roomLoading = false })
    },
    getRoomsData() {
      if (!Array.isArray(this.roomDateRangeValue)) { this.loadRoomStatus(); return }
      this.roomLoading = true
      operationGetRooms({
        option: 0,
        storeSN: this.storeSN,
        roomDateRangeValue: this.roomDateRangeValue
      }).then(res => {
        if (res && res.code === 200) {
          this.roomData = res.data || {}
          const dateRange = this.roomData['dateRange'] || []
          /* 【P180】列宽 78 → 84，与 CSS 中 .date-item/.day-cell 的 84px 保持一致
             （对齐销售端）。⚠️ 此值与 CSS 必须同步改，否则 scroll-content 宽度
             与实际列宽不符 ⇒ 横向滚动尽头会多出/少掉一截空白。 */
          this.tableContainerWith = dateRange.length * 84
          this.$nextTick(this.measureRoomScrollH)
        }
      }).catch(() => {}).then(() => { this.roomLoading = false })
    },
    /**
     * 【P176】动态计算房态滚动区高度：
     * 此前写死 calc(100vh - 168px)，未计入「无底部 Tab 栏 / 图例折叠」等场景，实测底部空出 53px。
     * 改为量测「滚动区顶部距视口顶部」后用剩余高度填满。
     * ★ 房态改为内联 Tab 页后底部 Tab 栏常驻 ⇒ 必须再减掉 Tab 栏高度，否则最后一行被压住。
     */
    measureRoomScrollH() {
      const el = this.$el && this.$el.querySelector ? this.$el.querySelector('.room-status-page .scroll-wrapper') : null
      if (!el) { this.roomScrollH = ''; return }
      const top = el.getBoundingClientRect().top
      const bar = this.$el.querySelector ? this.$el.querySelector('.van-tabbar') : null
      const barH = bar ? bar.getBoundingClientRect().height : 0
      const h = Math.max(240, Math.floor(window.innerHeight - top - barH))
      this.roomScrollH = h
    },
    searchStore(sn) {
      this.storeSN = sn
      this.getRoomsData()
    },
    /* 【P170】日期搜索（1:1 移植销售端 client.vue）：
       点 header → 打开「选择时间」对话框 → 点开始/结束时间 → 底部日期选择器 → 确定 → searchConfirm 重拉网格 */
    handleDialogDate() {
      if (!this.beignDate) {
        this.beignDate = (this.roomDateRangeValue && this.roomDateRangeValue[0]) || ''
        this.endDate = (this.roomDateRangeValue && this.roomDateRangeValue[1]) || ''
      }
      this.dialogDatePop = true
    },
    selectDate(type) {
      this.dialogDateType = type
      const cur = type === 0 ? this.beignDate : this.endDate
      this.currentDate = cur ? new Date(String(cur).replace(/-/g, '/')) : new Date()
      this.dialogDate = true
    },
    dateConfirm(value) {
      this.dialogDate = false
      const date = new Date(value || this.currentDate)
      const y = date.getFullYear()
      const m = date.getMonth() + 1
      const d = date.getDate()
      const formattedDate = y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0')
      if (this.dialogDateType === 0) { this.beignDate = formattedDate }
      if (this.dialogDateType === 1) { this.endDate = formattedDate }
    },
    dateCancel() {
      this.dialogDate = false
    },
    searchConfirm() {
      if (!this.beignDate || !this.endDate) { this.$toast('请选择起止日期'); return }
      if (String(this.beignDate) > String(this.endDate)) { this.$toast('开始时间不能晚于结束时间'); return }
      this.roomDateRangeValue = [this.beignDate, this.endDate]
      this.getRoomsData()
    },
    /**
     * 【P183】★★ `roomCellTone()` 已【整体删除】。
     * 原实现（P169 引入 / P178 修注释）判定口径为：
     *   hasContract = !!order.orders_main || !!order.contracts_sn
     *   paid        = Number(order.is_payment) === 1
     *   ⇒ hasContract ? (paid ? 3 绿 : 1 蓝) : (amt > 0 ? 2 黄 : 4 红)
     * 与销售端 client.vue 的四色条件（依赖 contracts.receivables / amount）**语义正交**：
     *   · orders_main 来自 parent_sn→t_pro_orders，实测覆盖率 4566/4656 ≈ 98% ⇒ 恒 truthy ⇒ 永落 3/1
     *   · contracts 来自 contracts_sn→t_pro_contracts，全库填充率仅 1113/4656 ≈ 24% ⇒ 销售端常落 2/4
     * 实测同一批 64 格两端 **0 格一致**（销售端全黄 / 运营端全绿）。
     * 按用户选定「完全跟随销售端」，改由模板内 4 个显式 v-if 直判，与 client.vue 逐字同构。
     */
    /** 【P169】只读详情弹窗（销售端 handlDetail 同源，去掉编辑/合同入口） */
    /* 【P339 BUG 修复】房态格子取订单 sn 的安全 accessor。
     * 原模板写死 `@click="openRoomDetail(item3['order']['sn'])"`：
     *   · Vue2 里 @click 表达式在**同一轮渲染**就会求值，即使配套的 v-if 为 false，
     *     `item3['order']` 为 null 时 `null['sn']` 直接抛
     *     「Cannot read properties of null (reading 'sn')」⇒ 点房态任意格子就崩。
     *   · 且后端 /operation/roomStatus 实测返回**扁平行**（根本没有 order 键），
     *     两种结构都要兼容。
     * 取不到 sn 时返回空串，openRoomDetail 内部已有 !sn 早退保护。 */
    roomCellSn(cell) {
      if (!cell) { return '' }
      const o = cell['order']
      if (o && o['sn']) { return String(o['sn']) }
      /* 扁平结构兜底：格子自身带 sn */
      if (cell['sn']) { return String(cell['sn']) }
      return ''
    },
    openRoomDetail(sn) {
      if (!sn) return
      this.currentDetailSn = sn
      this.orderDetailData = {}
      this.detailSvc = 0
      /* 【P335】打开新详情时归零 */
      this.detailStage = 1
      this.detailStageText = '待执行'
      /* 【P401】can_confirm 归 null（未加载）—— 打开详情瞬间不渲染「我知道了」，
         等 syncDetailSvc 拿到后端判据后再决定。避免非 owner 看到一闪而过的可点按钮。 */
      this.detailCanConfirm = null
      this.detailCanConfirmReason = ''
      /* 【P405】支付合同 / 完成服务可见性一并归零，等 syncDetailSvc 回填 */
      this.detailPaidContract = null
      this.detailCanFinish = null
      this.detailFinishReason = ''
      this.detailContractStage = null
      this.detailContractStageText = ''
      /* 【P232-fix】★ 详情弹窗（dialogDetail）实际渲染在**房态 Tab**（tabIndex===0）的
         v-if 容器内（index.vue:267-499），而消息页是 tabIndex===4 ⇒ 直接开弹层，DOM 不会生成。
         所以：**先切到房态 Tab，等 DOM 就绪后再开弹层**。
         （实测过切到 Tab1「会议室订单」也不行 —— 那里只有 bill-list，不含该弹窗。）
         【P234】msgReturnTab 记录来源页（消息=4 / 快捷=5），
         关闭弹层后由 closeDetail() 自动退回，避免「点条消息就莫名跳到房态页」。 */
      const openAfterTab = () => {
        this.dialogDetail = true
        /* 【P442】_from=operation 告知后端「这是运营端房态详情」，
           销售价格按运营口径过滤（仅运营条线部门负责人可见）。 */
        getClientOrderDetailData({ sn: sn, _from: 'operation' }).then(res => {
          if (res && res.code === 200) {
            this.orderDetailData = res.data || {}
          }
        }).catch(() => {})
        /* 服务状态：走运营端自己的详情接口 */
        this.syncDetailSvc(sn);
      }
      if (this.tabIndex !== 0) {
        this.subKey = ''
        this.tabIndex = 0
        this.subSeq++
        this.$nextTick(() => { this.$nextTick(openAfterTab) })
        return
      }
      openAfterTab()
    },
    /* 【P232】拉取并设置详情页的服务状态（打开详情与确认后复用） */
    syncDetailSvc(sn) {
      operationRoomOrderDetail({ sn: sn }).then(res => {
        if (res && res.code === 200 && res.data && res.data.room) {
          const room = res.data.room
          this.detailSvc = Number(room.service_status || 0)
          /* 【新功能】终态展示：服务人数 + 备注 */
          this.detailHeadcount = room.service_headcount ? Number(room.service_headcount) : 0
          this.detailFinishRemark = room.service_finish_remark || ''
          /* 【P335】读 stage / 合同态（P333 后端新增） */
          this.detailStage = Number(room.stage || 1)
          this.detailStageText = room.stage_text || ''
          this.detailContractStage = (room.contract_stage === null || room.contract_stage === undefined) ? null : Number(room.contract_stage)
          this.detailContractStageText = room.contract_stage_text || ''
          /* 【P401】★ 消费后端下发的 can_confirm / can_confirm_reason
           *   后端判据 = ownsRoomOrder(订单, 当前登录人) && service_status < 2
           *   （与 confirmTodo 的门禁**同一函数**，不会再出现「前端说能点、后端拒掉」）
           *   can_confirm 缺失（老后端）时保守取 false，宁可少一个按钮也不让人点了报错。 */
          const cc = res.data.can_confirm
          this.detailCanConfirm = (cc === 1 || cc === true || cc === '1')
          this.detailCanConfirmReason = res.data.can_confirm_reason || ''
          /* 【P405】★ 支付合同门禁 + 「完成服务」可见性
           *   has_paid_contract=0 ⇒ 整块动作区不渲染（后端同源判据）
           *   字段缺失（老后端）时保持 null，按「不拦截」处理，避免把按钮全藏掉 */
          const hp = res.data.has_paid_contract
          this.detailPaidContract = (hp === 0 || hp === false || hp === '0') ? false
            : ((hp === 1 || hp === true || hp === '1') ? true : null)
          const cf = res.data.can_finish_service
          this.detailCanFinish = (cf === 0 || cf === false || cf === '0') ? false
            : ((cf === 1 || cf === true || cf === '1') ? true : null)
          this.detailFinishReason = res.data.can_finish_reason || ''
        }
      }).catch(() => {})
    },
    pushTodoFromDetail() {
      this.onPushTodo({ sn: this.currentDetailSn })
    },
    /* 【P232】详情内「确认(我知道了)」→ 通知运营负责人；确认后刷新详情状态显示为已确认
       【P401】① 文案由「通知上级」改为「通知运营负责人」（后端已收敛为单人）
                ② 前置拦截：can_confirm 明确为 false 时不再发请求（按钮本已隐藏，
                   这里兜住键盘/冒泡等异常路径，避免又出现「报错被拦截」的观感） */
    confirmFromDetail() {
      const sn = this.currentDetailSn;
      if (!sn) { this.$toast('缺少订单 sn'); return }
      if (this.detailCanConfirm === false) {
        this.$toast(this.detailCanConfirmReason || '仅该订单负责人可确认（我知道了）', 'fail');
        return
      }
      this.doConfirm([sn], '已确认，已通知运营负责人');
      const t = setTimeout(() => { this.syncDetailSvc(sn); clearTimeout(t) }, 900);
    },
    /* 【P234/P235】关闭详情弹层：退回消息页，而不是停在房态页 */
    closeDetail() {
      this.dialogDetail = false
      const back = this.msgReturnTab
      this.$nextTick(() => {
        this.subKey = ''
        /* 消息页现在是 subKey 子页 ⇒ 必须回到 subKey 态 */
        this.tabIndex = (back === 5) ? 4 : back
        if (back === 4) {
          this.subKey = 'message'
          this.loadMessages(true)
        }
      })
    },
    finishFromDetail() {
      /* 【P405】前置拦截：按钮本已按 can_finish_service 隐藏，这里兜住
         键盘/冒泡/快速连点等异常路径，避免又变成「点了才报错」。 */
      if (this.detailPaidContract === false) {
        this.$toast('该订单未产生支付合同，无需完成服务', 'fail');
        return
      }
      if (this.detailCanFinish === false) {
        this.$toast(this.detailFinishReason || '当前不可完成服务', 'fail');
        return
      }
      this.onFinish({ sn: this.currentDetailSn })
    },
    /* 【新功能】打开「标记完成」弹框（stage>=3 才出现） */
    openCompleteDialog() {
      /* 【P339 修复】前置校验原读 `this.detailSvc !== 3`（service_status 口径），
       * 与模板判据（stage）不同源 ⇒ service_status 未同步时点了会误报「请先完成服务」，
       * 用户观感同样是「点了没反应」。现统一以 stage 为准，并兼容 service_status。 */
      const stage = Number(this.detailStage || 1)
      const svc = Number(this.detailSvc || 0)
      /* 【P405】未产生支付合同 ⇒ 不该有终态动作（按钮已隐藏，这里兜住异常路径） */
      if (this.detailPaidContract === false) {
        this.$toast('该订单未产生支付合同，无需完成服务', 'fail')
        return
      }
      /* 【P404】已终态（service_status=4）不再弹填写框：
         后端 completeService 对 svc>=4 是幂等跳过，弹了也只会回
         「该订单已完成（幂等跳过）」⇒ 白填一次。 */
      /* 【P411】★ 上面 P404 的限制已撤销：用户 10-21 要求「可以一直修改」。
         后端 completeService 在 svc>=4 时改为「修改模式」（只覆盖人数/备注，
         不改状态、不重复通知）⇒ 这里放开拦截，并进入编辑态 + 回填已有值。 */
      if (stage < 3 && svc < 3) {
        this.$toast('请先完成服务')
        return
      }
      this.completeEditMode = (svc >= 4)
      if (this.completeEditMode) {
        /* 回填上次填写的值，便于在原有基础上改（detailHeadcount 为 0 时留空） */
        this.completeHeadcount = this.detailHeadcount ? String(this.detailHeadcount) : ''
        this.completeRemark = this.detailFinishRemark || ''
      } else {
        this.completeHeadcount = ''
        this.completeRemark = ''
      }
      this.completeShow = true
    },
    /* 【新功能】提交完成：服务人数 + 备注 → 置 state=4 + 知会管理者链 */
    submitComplete() {
      const sn = this.currentDetailSn
      if (!sn) { this.$toast('缺少订单 sn'); return }
      const headcount = parseInt(this.completeHeadcount, 10)
      if (isNaN(headcount) || headcount < 0) {
        this.$toast('请填写有效的服务人数')
        return
      }
      this.completeSubmitting = true
      operationRoomComplete({ sn: sn, service_headcount: headcount, remark: this.completeRemark })
        .then(res => {
          const d = res && res.data
          if (res && res.code === 200 && d && d.ok) {
            suppressGlobalError()
            /* 【P411】编辑态：不重复通知，文案也要与事实一致 */
            this.$toast(d.edit ? '服务人数已更新' : '订单已完成，已通知管理者')
            this.completeShow = false
            this.refreshCurrent()
            this.syncDetailSvc(sn)
            /* 【P393-④】同 onFinish：立即消泡，不等 loadUnread 二次请求 */
            this.applyUnreadFromResponse(d)
          } else {
            this.$toast((res && res.msg) || '操作失败')
          }
        })
        .catch(() => {})
        .finally(() => { this.completeSubmitting = false })
    }
  }
}
</script>

<style scoped>
.op-page { min-height: 100vh; background: var(--page-bg, #f5f6f8); padding-bottom: 60px; }

/* 【P304】消息态：.op-page 改为 flex 列 + 自身不滚，
   高度封顶 100vh（不再 min-height），让 .op-msgwrap 内的 van-pull-refresh
   成为唯一滚动容器。
   ⚠ 只在「消息子页」生效：其它 Tab（房态/订单/采购）保持原 min-height + 页面级滚动，
     不动它们的手感。 */
.op-page--msg{
  height:100vh; min-height:0;
  display:flex; flex-direction:column;
  overflow:hidden; padding-bottom:0;
}

/* ============================================================
 * 【P304】★★ 运营端消息页滚动容器（修「不能滑动 + 上滑分页失效」）
 *   根因：#app/body/html 都是 height:100% + overflow:hidden（lib.min.css），
 *         .op-page 自身 overflow:visible ⇒ 全链无滚动容器，
 *         van-list 找不到可滚祖先 ⇒ 永不上报触底；页面也划不动。
 *   方案（财务端 P296 / 销售端同口径）：
 *     .op-msgwrap        自身不滚，只负责 flex 撑高并裁掉溢出
 *     .van-pull-refresh  成为**唯一**滚动容器（overflow-y:auto）
 *     .mp                占满可视区，.mp-tabs 吸顶、.mp-list 自然增长
 *   ⚠ 必须只给消息页加，否则会改变房态/订单/采购各 Tab 既有滚动手感。
 * ============================================================ */
.op-msgwrap{
  display:flex; flex-direction:column;
  flex:1 1 auto; min-height:0;
  overflow:hidden;
  box-sizing:border-box;
}
.op-msgwrap > .mp{ flex:1 1 auto; min-height:0; display:flex; flex-direction:column; }
/* van-pull-refresh 自己即滚动容器。
   ⚠ 两条硬约束（都是实测踩出来的）：
   ① Vant 的 `.van-pull-refresh{overflow:hidden}` 是**同属性**硬编码，
      必须用 `> ` 直接子选择器提高 specificity，否则会被它盖掉（computed 仍是 hidden）。
      ★ 不要写后代选择器 `.op-msgwrap .van-pull-refresh` —— 经 postcss 转换后
        overflow-y 会被裁掉，实测只留下 overscroll-behavior-y（财务端用 `>` 就正常）。
   ② track 也要能滚：van-list 的 getScroller 从 .van-list 自身向上找，
      第一个 overflow-y 命中的就是 track。 */
.op-msgwrap > .mp > .van-pull-refresh{
  flex:1 1 auto; min-height:0;
  overflow-y:auto;
  -webkit-overflow-scrolling:touch;
  overscroll-behavior-y:contain;
}
.op-msgwrap > .mp > .van-pull-refresh > .van-pull-refresh__track{
  display:flex; flex-direction:column; flex:1 1 auto; min-height:0;
  height:auto; overflow-y:auto;
  -webkit-overflow-scrolling:touch;
  box-sizing:border-box;
}
/* 顶部条 / Tab 吸顶不参与 flex:1 */
.op-msgwrap .mp-top{ flex:0 0 auto; }
.op-msgwrap .mp-tabs{ flex:0 0 auto; position:sticky; top:0; z-index:6; }
/* 列表区允许自然增长并占满剩余高度；底部为 tabbar 留出 66px */
.op-msgwrap .mp-list{ flex:1 1 auto; padding-bottom:66px; }
.op-topback { margin-right: 6px; font-size: 18px; color: #323233; }
/* 【P343-UI】「实际服务人数 / 服务备注」并入会议室预定信息卡片后的配套样式。
   放在「常用联系人」之后、「增值业务」之前，视觉上属于会议室预定信息的延续。 */
.detail-popup .detail-card__row--done {
  margin-top: 2px; padding-top: 6px;
  border-top: 1px dashed #ebedf0;
}
.detail-popup .op-done-tag {
  display: inline-block; margin-right: 6px;
  padding: 1px 7px; border-radius: 9px;
  background: #e8f4ff; color: #1677ff;
  font-size: 11px; font-weight: 500;
}
.detail-popup .op-done-num { font-weight: 600; color: #1f2733; }
.detail-popup .detail-card__val--muted { color: #aab2bd; }

/* 【新功能】完成弹框（state=4） */
.complete-popup{ width:100%; }
.complete-popup__hd{ padding:18px 16px 12px; border-bottom:1px solid #f0f1f3; }
.complete-popup__title{ font-size:16px; font-weight:700; color:#1f2329; }
.complete-popup__sub{ margin-top:8px; font-size:12px; color:#8c8f94; line-height:1.6; }
.complete-popup__bd{ padding:6px 0; }
.complete-popup__ft{ display:flex; gap:10px; padding:12px 16px calc(12px + env(safe-area-inset-bottom)); }
.complete-popup__ft .van-button{ flex:1; border-radius:8px; }

/* 权限不足 */
.op-denied { padding: 60px 20px 20px; text-align: center; }
.op-denied__ic { font-size: 52px; color: #f0a020; margin-bottom: 12px; }
.op-denied__t { font-size: 18px; font-weight: 700; color: #1d2129; margin-bottom: 8px; }
.op-denied__m { font-size: 13px; color: #8c8f94; margin-bottom: 18px; }
.op-denied__card { background: #fff; border-radius: 16px; padding: 14px 16px; margin-bottom: 14px; text-align: left; box-shadow: 0 2px 12px rgba(20,40,80,.06); }
.op-denied__row { display: flex; justify-content: space-between; font-size: 13px; color: #646566; padding: 6px 0; }
.op-denied__row b { color: #1d2129; font-weight: 600; }
.op-denied__hint { font-size: 12px; color: #969799; line-height: 1.7; margin-bottom: 20px; text-align: left; }

/* Hero */
/* 【P114】与财务端 .fn2-hero 同一套规格（渐变 / 头像 / 字号） */
.op-hero { background: linear-gradient(135deg, #2b5fd9 0%, #4a86f0 100%); padding: 20px 16px 18px; color: #fff; border-radius: 0 0 20px 20px; }
.op-hero__top { display: flex; align-items: center; gap: 12px; }
.op-avatar { width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,.22); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 600; flex: 0 0 44px; }
.op-hero__id { flex: 1; min-width: 0; }
.op-hero__name { font-size: 17px; font-weight: 600; }
.op-hero__dept { font-size: 12px; opacity: .82; margin-top: 3px; }
.op-hero__role { font-size: 12px; background: rgba(255,255,255,.22); border-radius: 12px; padding: 3px 10px; flex: 0 0 auto; }
.op-kpis { display: flex; gap: 10px; margin-top: 16px; }
.op-kpi { flex: 1; background: rgba(255,255,255,.16); border-radius: 14px; padding: 12px 10px; text-align: center; position: relative; }
.op-kpi__n { font-size: 20px; font-weight: 700; }
.op-kpi__dot { position: absolute; top: 10px; right: 14px; width: 7px; height: 7px; border-radius: 50%; background: #ff4d4f; }
.op-kpi__l { font-size: 11.5px; opacity: .88; margin-top: 3px; }

/* 快捷入口 */
.op-quick { background: #fff; border-radius: 16px; margin: 12px; padding: 14px 14px 8px; box-shadow: 0 2px 12px rgba(20,40,80,.06); }
.op-quick__hd { font-size: 15px; font-weight: 700; color: #1d2129; display: flex; align-items: center; gap: 7px; padding-bottom: 10px; }
.op-quick__hd::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: #2b5fd9; }
.op-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 4px; }
.op-tile { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 12px 4px; border-radius: 12px; }
.op-tile:active { background: #f2f6ff; }
.op-tile__ic { position: relative; width: 42px; height: 42px; border-radius: 13px; background: #eef2fb; display: flex; align-items: center; justify-content: center; }
.op-tile__svg svg { stroke: #2b5fd9; }
.op-tile__dot { position: absolute; top: -4px; right: -4px; min-width: 17px; height: 17px; line-height: 17px; padding: 0 4px; border-radius: 9px; background: #ff4d4f; color: #fff; font-size: 10.5px; text-align: center; }
.op-tile__label { font-size: 12.5px; color: #323233; }
.op-tip { font-size: 11.5px; color: #969799; padding: 10px 2px 8px; line-height: 1.6; }

/* 子页 */
/* 【P235】子页顶部返回条（消息中心）：底部 Tab 栏在子页仍显示，这里给一条明确的退出入口 */
.op-sub-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 0.5px solid var(--border, #ebedf0);
  position: sticky;
  top: 0;
  z-index: 12;
}
.op-sub-bar__back { font-size: 18px; color: #323233; }
.op-sub-bar__t { font-size: 15px; font-weight: 500; color: #323233; }
.op-sub-bar__r { margin-left: auto; font-size: 13px; color: #2b5fd9; }

.op-sub { padding-bottom: 20px; }
/* 【P176】内联 Tab 页（房态/快捷入口）：底部要留出 Tab 栏高度，否则内容被 Tab 栏压住 */
.op-tabpage { padding-bottom: 60px; }
.op-date { flex: 1; border: 1px solid var(--border, #ebedf0); border-radius: 8px; padding: 6px 10px; font-size: 13px; color: #323233; background: #fff; }

/* ============================================================================
 * 【P169】房态网格（1:1 移植自销售端 client.vue，仅作用域收窄到 .room-status-page）
 * ========================================================================== */
.room-status-page { background: #f5f7fa; color: #333; -webkit-tap-highlight-color: transparent; }
.room-status-board { display: flex; flex-direction: column; background: #f5f7fa; }
.room-status-board * { margin: 0; padding: 0; box-sizing: border-box; }

/* 顶部日期导航 */
.room-status-page .header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; position: sticky; top: 0; z-index: 99; font-size: 14px;
  box-shadow: 0 2px 8px rgba(59,111,212,.18);
}
.room-status-page .header__date { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.room-status-page .header__cal { font-size: 16px; }
.room-status-page .header__range i { font-style: normal; opacity: .7; margin: 0 2px; }
.room-status-page .header__btn {
  display: flex; align-items: center; gap: 2px;
  background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.35);
  padding: 5px 12px; border-radius: 16px; font-size: 13px;
}

/* 门店横向滚动 */
.room-status-page .store-wrap {
  background: #fff; padding: 10px 12px; overflow-x: auto; white-space: nowrap;
  border-bottom: 1px solid #eee;
}
.room-status-page .store-wrap::-webkit-scrollbar { display: none; }
.room-status-page .store-item {
  display: inline-block; padding: 7px 14px; margin-right: 8px; border-radius: 16px;
  font-size: 13px; background: #eef2f7; color: #5b6472; border: 1px solid transparent;
  transition: all .15s ease;
}
/* 【P176】明确未选中态底色（此前 background 简写被 gradient 规则牵连，实测 backgroundColor 透明） */
.room-status-page .store-item { background-color: #eef2f7; }
.room-status-page .store-item.active {
  background-color: #3b6fd4;
  background-image: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; border-color: #3b6fd4;
  box-shadow: 0 3px 8px rgba(59,111,212,.28);
}

/* 【P180】图例条样式（.room-legend / .dot--*）已随模板整体删除。
   四色本身由 .content_status_1~4 定义，与图例无关，故保留不动。 */

/* 横向滚动区（日期表头 + 内容同步滚动）
   【P176】高度改为「动态 calc 变量」，由 JS 按实际可用视口计算（见 roomScrollH），
   不再写死 168px —— 此前在无底部 Tab 栏的场景会底部空出 ~53px。 */.room-status-page .scroll-wrapper {
  overflow-x: auto; overflow-y: auto;
  height: var(--room-scroll-h, calc(100vh - 210px));
  overscroll-behavior: none; background: #f5f7fa;
  -webkit-overflow-scrolling: auto;
}
.room-status-page .scroll-wrapper::-webkit-scrollbar { display: none; }
.room-status-page .scroll-content { position: relative; min-height: 100%; }

/* 会议室名列（左固定）
   【P176】行高由「格子撑开」改为「等分铺满」，避免出现 276px 行却有大量空白
   【P180】★ 尺寸 1:1 对齐销售端 client.vue（88px / 13px / padding 10px）：
     此前为提升信息密度压到 72px + 12px，导致左侧房间名被右侧格子「挤压」成窄条，
     用户反馈「左边的标签有问题把信息挤压了」。房间名是固定的短文本（H1/H2/H3），
     不随密度收益而需要压缩，故恢复销售端口径。
     ⚠️ 左侧列宽若与右侧 day-cell 宽度（84px）不成套，滚动时视觉上会「错位」。 */
.room-status-page .room-name {
  position: sticky; left: 0; z-index: 5;
  padding: 10px 10px; border-bottom: 1px solid #eef0f3;
  width: 88px; min-width: 88px;
  font-size: 13px; font-weight: 700; color: #2b3340;
  background: #eef2f7; display: flex; align-items: center;
  box-shadow: 2px 0 6px rgba(0,0,0,.04);
}

/* 日期表头（上固定）
   【P180】列宽 78 → 84，与销售端 .date-item 一致（配合左侧 88px 列宽成套） */
.room-status-page .date-header {
  position: sticky; top: 0; background: #fff; display: flex;
  border-bottom: 1px solid #eef0f3; z-index: 10;
}
.room-status-page .date-item {
  min-width: 84px; padding: 12px 5px; text-align: center;
  font-size: 12px; line-height: 16px; color: #5b6472; border-right: 1px solid #eef0f3;
  flex: 0 0 84px;
}
.room-status-page .date-item.today { color: #3b6fd4; font-weight: 700; background: #eef4ff; }

/* 房态列表 */
.room-status-page .room-list { background: #fff; }
/* 【P181】★★ 关键修复：`.room-row` 必须为 display:block（销售端口径），
   ⚠️ 此前运营端写的是 `display:flex` ⇒ 导致 `.room-name`（sticky/88px）被拉成
      **整行高度的左侧竖列**（实测高 276px），把会议室名 H1/H2 变成左侧竖条，
      与销售端「H1 是横贯整行的**横幅条**、格子在其下方」完全不符。
   ⇒ 改回 block 后：`.room-name` 恢复为一行 38.5px 高的横幅，
      `.room-days`（flex）自然流到其**下方**并从 x=0 起排，与销售端 1:1 一致。 */
.room-status-page .room-row { border-bottom: 1px solid #eef0f3; display: block; }
.room-status-page .room-days { display: flex; }
/* 【P180】day-cell 78 → 84 宽；min-height 58 → 96，对齐销售端 */
.room-status-page .day-cell {
  display: flex; flex-direction: column; align-items: stretch; justify-content: center;
  min-width: 84px; flex: 0 0 84px; padding: 6px 5px;
  border-right: 1px solid #eef0f3; min-height: 96px;
}
/* 【P180】格子高 46 → 82、圆角 8 → 10、padding 6px、阴影对齐销售端。
   ⚠️ status 高（82）必须 ≤ day-cell min-height（96）且留出上下 margin，
      否则 3 段时段的格子会溢出被裁（此前 46px 高正是为塞进 58px 的 day-cell 才压的）。 */
.room-status-page .status {
  display: flex; flex-direction: column; justify-content: center;
  border-radius: 10px; font-size: 12px; margin: 3px 0; text-align: center;
  height: 82px; padding: 8px 6px; box-shadow: 0 2px 6px rgba(0,0,0,.08);
  box-sizing: border-box; overflow: hidden;
}
.room-status-page .status .item_name {
  width: 100%; height: 18px; line-height: 18px;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.room-status-page .status .item_name:first-child { font-weight: 700; font-size: 12px; height: 20px; line-height: 20px; }
.room-status-page .status .item_name:not(:first-child) { opacity: .92; font-size: 11px; height: 16px; line-height: 16px; margin-top: 2px; }
.room-status-page .status_color { font-size: 12px; color: #fff; }
.room-status-page .status--empty {
  background: #f3f5f8; border: 1px dashed #dfe3e8; box-shadow: none; color: #aab2bd;
}
.room-status-page .status--empty__txt { font-size: 11px; }
.room-status-page .content_status_1 { background-color: #409EFF; }
.room-status-page .content_status_2 { background-color: #f59e0b; }
.room-status-page .content_status_3 { background-color: #67C23A; }
.room-status-page .content_status_4 { background-color: #F56C6C; }

/* 【P181】账号条样式（.op-acct*）与退出弹窗样式（.op-logout*）已随模板整体删除。
   历史：P180 为「房态首屏补退出出口」而新增，现用户明确要求去掉名称与退出。 */

/* ============================================================================
 * 【P196】账号 / 退出登录（1:1 对齐销售端 client.vue 的「账号」区块）
 *   ① .acct-row* 全部数值（尺寸/圆角/色值/间距/字号）与 client.vue L8506-8515 逐字一致；
 *   ② 外层容器沿用运营端既有 .op-quick 白卡（与「快捷入口」卡同宽同边距，视觉成组），
 *      销售端用的是自己的 .quick-section--acct，两者卡片外观等价；
 *   ③ .acct-list 左右内边距补到 10px，与销售端 .acct-list{padding:2px 10px 4px} 对齐
 *      ⇒ 使 .acct-row 相对屏幕的左右留白与销售端完全相同。
 * ========================================================================== */
.op-quick--acct { padding: 14px 14px 12px; margin-top: 0; }
/* 销售端标题只有「竖条 + 文字」，运营端 .op-quick__hd 默认是圆点前缀 ⇒ 此处换回竖条 */
.op-quick__hd--plain::before {
  content: ""; width: 3px; height: 14px; border-radius: 2px; background: #2b5fd9;
}
.acct-list { padding: 2px 4px 4px; }
.acct-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 10px; border-radius: 12px;
  background: #fafbfc; border: 1px solid #ebedf0;
}
.acct-row:active { transform: scale(.99); background: #eef2fb; }
.acct-row__ic {
  width: 34px; height: 34px; border-radius: 11px; background: #eef2fb;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.acct-row__ic svg {
  width: 19px; height: 19px; stroke: #2b5fd9; fill: none;
  stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round;
}
.acct-row__txt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.acct-row__label { font-size: 14px; font-weight: 600; color: #323233; }
.acct-row__sub { font-size: 11.5px; color: #969799; }
.acct-row__arrow { color: #c8c9cc; font-size: 14px; flex-shrink: 0; }

/* 房态详情弹窗（专业卡片式） */
.detail-popup { border-radius: 16px 16px 0 0; }
/* 【P170】日期搜索弹窗（与销售端 .dialogDate 同结构，补齐可读样式） */
.dialogDate { width: 100%; padding: 4px 16px 16px; }
.dialogDateRow {
  display: flex; align-items: center; justify-content: space-between;
  min-height: 48px; padding: 0 12px; margin-top: 8px;
  background: #f7f8fa; border-radius: 10px;
}
.dialogDateLable { font-size: 14px; color: #646566; }
.dialogDateValue { font-size: 14px; color: #1d2129; font-weight: 600; }
.dialogDateValue:empty::after { content: '请选择'; color: #c8c9cc; font-weight: 400; }

.detail-popup .van-popup__close-icon { display: none; }
.detail-popup .detail-sheet { display: flex; flex-direction: column; height: 100%; background: #f5f7fa; }
.detail-popup .detail-sheet__hd {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; border-radius: 16px 16px 0 0;
}
.detail-popup .detail-sheet__title { font-size: 16px; font-weight: 700; }
.detail-popup .detail-sheet__close { font-size: 20px; color: #fff; }
.detail-popup .detail-sheet__body {
  flex: 1; overflow-y: auto; padding: 14px 14px 24px;
}
/* 【P339-UI】原这里有两条重复的 `.detail-popup .detail-hero` 定义（2178/2182），
   旧的那条漏删导致规则靠「后者覆盖前者」生效，极易再踩坑，已合并为一条。 */
.detail-popup .detail-hero {
  display: flex; flex-wrap: wrap; align-items: flex-start;
  background: #fff; border-radius: 14px;
  padding: 14px; box-shadow: 0 2px 10px rgba(0,0,0,.05); margin-bottom: 14px;
}
/* 【P339-UI】客户名块：占满整行（flex-basis:100%），
   不再与标签抢同一行 ⇒ 长客户名不再被压成竖排单字。
   word-break: break-word 只在超长无空格串处断行，中文正常折行不逐字断。 */
.detail-popup .detail-hero__main { flex: 1 1 100%; min-width: 0; }
.detail-popup .detail-hero__name {
  font-size: 16px; font-weight: 700; color: #1f2733;
  line-height: 1.45; word-break: break-word; overflow-wrap: anywhere;
}
.detail-popup .detail-hero__sub { font-size: 12.5px; color: #8b94a3; margin-top: 4px; }
/* 标签独立成行并允许换行；margin-left 负值抵消首标签的额外左间距 */
.detail-popup .detail-hero__tags {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  flex: 1 1 100%; margin-top: 10px;
}
.detail-popup .detail-hero__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.detail-popup .detail-sec { margin-bottom: 14px; }
.detail-popup .detail-sec__title {
  display: flex; align-items: center; font-size: 14px; font-weight: 700;
  color: #1f2733; margin: 4px 2px 10px;
}
.detail-popup .detail-sec__bar {
  width: 4px; height: 14px; border-radius: 2px;
  background: linear-gradient(180deg, #5b8def, #3b6fd4); margin-right: 8px;
}
.detail-popup .detail-cards { display: flex; flex-direction: column; gap: 10px; }
.detail-popup .detail-card { background: #fff; border-radius: 12px; padding: 12px 14px; box-shadow: 0 2px 8px rgba(0,0,0,.04); }
.detail-popup .detail-card--sm { padding: 10px 12px; }
.detail-popup .detail-card__head { font-size: 14px; font-weight: 700; color: #1f2733; margin-bottom: 8px; }
.detail-popup .detail-card__head em { font-style: normal; font-size: 12px; font-weight: 500; color: #8b94a3; margin-left: 6px; }
.detail-popup .detail-card__row { display: flex; font-size: 13px; padding: 3px 0; line-height: 1.5; }
.detail-popup .detail-card__label { width: 64px; flex-shrink: 0; color: #8b94a3; }
.detail-popup .detail-card__val { flex: 1; color: #2b3340; word-break: break-all; }
/* 【P312】备注富文本渲染：v-html 带进来的 p/ul/img 需要重置 */
.detail-popup .detail-rich { line-height: 1.7; }
.detail-popup .detail-rich p { margin: 0 0 4px; }
.detail-popup .detail-rich p:last-child { margin-bottom: 0; }
.detail-popup .detail-rich ul, .detail-popup .detail-rich ol { margin: 2px 0 2px 18px; padding: 0; }
.detail-popup .detail-rich img { max-width: 100%; border-radius: 6px; margin-top: 4px; }
.detail-popup .detail-rich table { width: 100%; border-collapse: collapse; margin-top: 4px; }
.detail-popup .detail-rich td, .detail-popup .detail-rich th { border: 1px solid #ebedf0; padding: 3px 5px; font-size: 12px; }
.detail-popup .detail-empty { background: #fff; border-radius: 12px; padding: 20px; text-align: center; color: #aab2bd; font-size: 13px; }

/* 服务状态标签 + 底部动作位 */
/* 【P335】订单阶段 / 合同完成态标签：与「服务状态」并列 */
.op-stage-chip { margin-left: 6px; }
.op-stage-chip--1 { background: #eef1f6; color: #6b7280; }
.op-stage-chip--2 { background: #fff3e0; color: #b26a00; }
.op-stage-chip--3 { background: #e8f4ff; color: #1677ff; }
.op-stage-chip--4 { background: #e9f7ef; color: #12874a; }
.op-stage-chip--con { background: #f3ecff; color: #6b3fd4; }
.op-svc { padding: 5px 12px; border-radius: 14px; font-size: 12px; font-weight: 600; }
.op-svc--0 { background: #f2f3f5; color: #969799; }
.op-svc--1 { background: #fff7e6; color: #d48806; }
.op-svc--2 { background: #e8f3ff; color: #3b6fd4; }
.op-svc--3 { background: #eaf7ea; color: #389e0d; }
.op-svc-ft { display: flex; gap: 10px; padding: 12px 4px 4px; }
.op-svc-ft .van-button { flex: 1; }
/* 【P401】不可确认原因（非订单负责人打开详情时展示，替代「点了才报错」） */
.op-svc-deny {
  margin: 10px 4px 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff7e6;
  color: #ad6800;
  font-size: 12px;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// src/pages/operation/index.vue